import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InvoiceTemplateEditor } from '../components/shared';
import ServiceManagement from '../components/ServiceManagement';
import { 
  Plus, 
  Users, 
  Building2, 
  Pencil, 
  Trash2, 
  Key,
  UserCheck,
  UserX,
  Shield,
  Coins,
  RefreshCw,
  Check,
  X,
  Database,
  Download,
  Upload,
  AlertTriangle,
  FileJson,
  HardDrive,
  FileText,
  Eye,
  Package,
  WifiOff,
  Hash,
  Calendar,
  Lock,
  Unlock,
  AlertCircle,
  Landmark,
  Search,
  Loader2,
  Save,
  Printer,
  Sparkles,
  ChevronDown,
  Terminal,
  Trash2 as TrashIcon,
  Clock,
  Info,
  Tag,
  Wrench,
  Scale,
  Calculator,
  ArrowRightLeft,
  TrendingUp,
  Bot,
  Zap,
  Pill,
  CheckCircle,
  Cloud
} from 'lucide-react';
import { APP_VERSION } from '../config/version';
import axios from 'axios';
import { getRoleDisplayName, formatDate } from '../lib/utils';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Document types for templates
const DOCUMENT_TYPES = [
  { value: 'sales_invoice', label: 'Sales Invoice', icon: FileText, color: 'text-blue-500' },
  { value: 'purchase_invoice', label: 'Purchase Invoice', icon: Package, color: 'text-green-500' },
  { value: 'sales_quotation', label: 'Sales Quotation', icon: FileText, color: 'text-purple-500' }
];

// Document Template Manager Component
const DocumentTemplateManager = ({ currentOrg, fetchOrganizations }) => {
  const [selectedDocType, setSelectedDocType] = useState('sales_invoice');
  
  // Default template configurations for each document type
  const DEFAULT_TEMPLATES = {
    sales_quotation: {
      page_width: 210,
      page_height: 297,
      company_name: currentOrg?.name || '',
      company_type: '',
      address: currentOrg?.address || '',
      tel_fax: currentOrg?.phone || '',
      mobile: '',
      email: currentOrg?.email || '',
      footer_text: 'Thank you for your business!',
      field_positions: [
        { field_name: 'invoice_number', x: 75, y: 5, font_size: 14, font_weight: 'bold', text_align: 'left' },
        { field_name: 'date', x: 75, y: 10, font_size: 11, font_weight: 'normal', text_align: 'left' },
        { field_name: 'due_date', x: 75, y: 14, font_size: 11, font_weight: 'normal', text_align: 'left' },
        { field_name: 'customer_name', x: 5, y: 22, font_size: 12, font_weight: 'bold', text_align: 'left' },
        { field_name: 'customer_address', x: 5, y: 27, font_size: 10, font_weight: 'normal', text_align: 'left' },
        { field_name: 'account_code', x: 5, y: 31, font_size: 10, font_weight: 'normal', text_align: 'left' },
        { field_name: 'company_name', x: 5, y: 5, font_size: 16, font_weight: 'bold', text_align: 'left' },
        { field_name: 'company_address', x: 5, y: 11, font_size: 10, font_weight: 'normal', text_align: 'left' },
        { field_name: 'registration_no', x: 5, y: 15, font_size: 10, font_weight: 'normal', text_align: 'left' },
        { field_name: 'subtotal', x: 75, y: 75, font_size: 11, font_weight: 'normal', text_align: 'right' },
        { field_name: 'total', x: 75, y: 85, font_size: 14, font_weight: 'bold', text_align: 'right' },
        { field_name: 'amount_in_words', x: 5, y: 90, font_size: 10, font_weight: 'normal', text_align: 'left' },
      ],
      line_items_config: {
        start_y: 38,
        row_height: 4,
        max_rows: 12
      },
      custom_elements: []
    },
    sales_invoice: {
      page_width: 210,
      page_height: 297,
      company_name: currentOrg?.name || '',
      field_positions: [],
      line_items_config: { start_y: 35, row_height: 3, max_rows: 10 },
      custom_elements: []
    },
    purchase_invoice: {
      page_width: 210,
      page_height: 297,
      company_name: currentOrg?.name || '',
      field_positions: [],
      line_items_config: { start_y: 35, row_height: 3, max_rows: 10 },
      custom_elements: []
    }
  };

  // Get the template for the selected document type
  const getTemplateForType = (docType) => {
    // First check document_templates (new structure)
    if (currentOrg?.document_templates?.[docType]) {
      return currentOrg.document_templates[docType];
    }
    // Fall back to legacy invoice_template for sales_invoice
    if (docType === 'sales_invoice' && currentOrg?.invoice_template) {
      return currentOrg.invoice_template;
    }
    // Return default template for the document type
    return DEFAULT_TEMPLATES[docType] || null;
  };
  
  // Save template for a specific document type
  const handleSaveTemplate = async (templateData) => {
    try {
      // Build the updated document_templates object
      const updatedTemplates = {
        ...(currentOrg?.document_templates || {}),
        [selectedDocType]: templateData
      };
      
      // For backwards compatibility, also update invoice_template when saving sales_invoice
      const updatePayload = {
        document_templates: updatedTemplates
      };
      if (selectedDocType === 'sales_invoice') {
        updatePayload.invoice_template = templateData;
      }
      
      await axios.put(`${API}/organizations/${currentOrg.id}`, updatePayload);
      await fetchOrganizations();
      alert(`${DOCUMENT_TYPES.find(d => d.value === selectedDocType)?.label} template saved successfully!`);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert(error.response?.data?.detail || 'Failed to save template');
      throw error;
    }
  };
  
  const currentTemplate = getTemplateForType(selectedDocType);
  const selectedDocInfo = DOCUMENT_TYPES.find(d => d.value === selectedDocType);
  
  // Check if this is a saved template or default
  const isSavedTemplate = currentOrg?.document_templates?.[selectedDocType] || 
    (selectedDocType === 'sales_invoice' && currentOrg?.invoice_template);
  
  return (
    <div className="space-y-4">
      {/* Document Type Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-2 block">Select Document Type</Label>
              <div className="flex flex-wrap gap-2">
                {DOCUMENT_TYPES.map(docType => {
                  const Icon = docType.icon;
                  const isSelected = selectedDocType === docType.value;
                  const hasTemplate = getTemplateForType(docType.value) !== null;
                  
                  return (
                    <Button
                      key={docType.value}
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedDocType(docType.value)}
                      className={`flex items-center gap-2 ${isSelected ? '' : docType.color}`}
                    >
                      <Icon className="w-4 h-4" />
                      {docType.label}
                      {hasTemplate && (
                        <Check className="w-3 h-3 text-green-400" />
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {isSavedTemplate ? (
                <span className="flex items-center gap-1 text-green-500">
                  <Check className="w-4 h-4" /> Template configured
                </span>
              ) : currentTemplate ? (
                <span className="flex items-center gap-1 text-blue-500">
                  <FileText className="w-4 h-4" /> Using default template
                </span>
              ) : (
                <span className="flex items-center gap-1 text-yellow-500">
                  <AlertTriangle className="w-4 h-4" /> No template
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Info Banner */}
      <div className="bg-muted/30 border rounded-lg p-3">
        <p className="text-sm text-muted-foreground">
          <strong className={selectedDocInfo?.color}>{selectedDocInfo?.label}</strong> template. 
          Each document type can have its own unique layout. Changes here only affect {selectedDocInfo?.label.toLowerCase()} printing.
        </p>
      </div>
      
      {/* Template Editor */}
      <InvoiceTemplateEditor
        key={selectedDocType} // Force re-render when type changes
        template={currentTemplate}
        organizationId={currentOrg?.id}
        organizationName={currentOrg?.name}
        onSave={handleSaveTemplate}
      />
    </div>
  );
};

// Label Options Manager Component for Prescription Labels
const LabelOptionsManager = ({ currentOrg }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('doseForm');
  const [newOption, setNewOption] = useState({ english: '', arabic: '' });

  const CATEGORIES = [
    { key: 'doseForm', label: 'Dose / Form', labelAr: 'الجرعة / الشكل' },
    { key: 'frequency', label: 'Frequency', labelAr: 'التكرار' },
    { key: 'foodRelation', label: 'Relation to Food', labelAr: 'العلاقة بالأكل' },
    { key: 'duration', label: 'Duration', labelAr: 'المدة' },
  ];

  const fetchOptions = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prescription-label-options/all?organization_id=${currentOrg.id}&include_inactive=true`);
      setOptions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch label options:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [currentOrg?.id]);

  const handleImportDefaults = async () => {
    if (!window.confirm('Import default prescription label options? This will add any missing default options.')) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/prescription-label-options/import-defaults?organization_id=${currentOrg.id}`);
      alert(res.data.message);
      await fetchOptions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to import defaults');
    } finally {
      setImporting(false);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.english.trim() || !newOption.arabic.trim()) {
      alert('Please fill both English and Arabic fields');
      return;
    }
    const key = newOption.english.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setSaving(true);
    try {
      await axios.post(`${API}/prescription-label-options`, {
        organization_id: currentOrg.id,
        category: selectedCategory,
        key,
        english: newOption.english.trim(),
        arabic: newOption.arabic.trim(),
      });
      await fetchOptions();
      setShowAddDialog(false);
      setNewOption({ english: '', arabic: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add option');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOption = async () => {
    if (!editingOption) return;
    setSaving(true);
    try {
      await axios.put(`${API}/prescription-label-options/${editingOption.id}`, {
        english: editingOption.english,
        arabic: editingOption.arabic,
      });
      await fetchOptions();
      setEditingOption(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update option');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;
    try {
      await axios.delete(`${API}/prescription-label-options/${optionId}`);
      await fetchOptions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete option');
    }
  };

  const getCategoryOptions = (categoryKey) => {
    return options.filter(o => o.category === categoryKey).sort((a, b) => a.sort_order - b.sort_order);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading label options...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Tag className="w-5 h-5 text-emerald-500" />
                Prescription Label Options
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Manage dropdown options for prescription label generation (Dose, Frequency, Food Relation, Duration)
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleImportDefaults}
                disabled={importing}
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Import Defaults
              </Button>
              <Button
                size="sm"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Option
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {options.length === 0 ? (
            <div className="text-center py-8 bg-muted/30 rounded-lg">
              <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">No label options found. Import defaults to get started.</p>
              <Button onClick={handleImportDefaults} disabled={importing}>
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Import Default Options
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {CATEGORIES.map(cat => (
                <div key={cat.key} className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/30 px-4 py-3 flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{cat.label}</h4>
                      <span className="text-sm text-muted-foreground font-arabic" dir="rtl">{cat.labelAr}</span>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      {getCategoryOptions(cat.key).length} options
                    </span>
                  </div>
                  <div className="divide-y">
                    {getCategoryOptions(cat.key).map(opt => (
                      <div key={opt.id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/20">
                        <div className="flex-1 grid grid-cols-2 gap-4">
                          <span className="text-sm">{opt.english}</span>
                          <span className="text-sm font-arabic text-right" dir="rtl">{opt.arabic}</span>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingOption({ ...opt })}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteOption(opt.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {getCategoryOptions(cat.key).length === 0 && (
                      <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                        No options in this category
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Option Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Label Option</DialogTitle>
            <DialogDescription>
              Add a new option to the prescription label dropdowns.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.key} value={cat.key}>
                      {cat.label} ({cat.labelAr})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>English Text</Label>
              <Input
                placeholder="e.g., 1 Tablet"
                value={newOption.english}
                onChange={(e) => setNewOption(prev => ({ ...prev, english: e.target.value }))}
              />
            </div>
            <div>
              <Label>Arabic Text</Label>
              <Input
                placeholder="e.g., حبة واحدة"
                className="font-arabic"
                dir="rtl"
                value={newOption.arabic}
                onChange={(e) => setNewOption(prev => ({ ...prev, arabic: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddOption} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Option
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Option Dialog */}
      <Dialog open={!!editingOption} onOpenChange={(open) => !open && setEditingOption(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Label Option</DialogTitle>
          </DialogHeader>
          {editingOption && (
            <div className="space-y-4 py-4">
              <div>
                <Label>English Text</Label>
                <Input
                  value={editingOption.english}
                  onChange={(e) => setEditingOption(prev => ({ ...prev, english: e.target.value }))}
                />
              </div>
              <div>
                <Label>Arabic Text</Label>
                <Input
                  className="font-arabic"
                  dir="rtl"
                  value={editingOption.arabic}
                  onChange={(e) => setEditingOption(prev => ({ ...prev, arabic: e.target.value }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingOption(null)}>Cancel</Button>
            <Button onClick={handleUpdateOption} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SettingsPage = () => {
  const { user, organizations, currentOrg, fetchOrganizations, token } = useAuth();
  const { fiscalYears, fetchFiscalYears, selectedFY } = useFiscalYear();
  const [users, setUsers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('all');
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [isCurrencyDialogOpen, setIsCurrencyDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteOrgConfirm, setDeleteOrgConfirm] = useState(null);
  const [deleteOrgForce, setDeleteOrgForce] = useState(false);
  const [deleteOrgError, setDeleteOrgError] = useState(null);
  const [deleteCurrencyConfirm, setDeleteCurrencyConfirm] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [seedingCurrencies, setSeedingCurrencies] = useState(false);
  
  // Backup & Restore state
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreFileRef = useRef(null);

  // Invoice Template state
  const [invoiceTemplate, setInvoiceTemplate] = useState({
    company_name: '',
    company_type: 'S.A.R.L.',
    tel_fax: '',
    mobile: '',
    email: '',
    address: '',
    logo_url: '',
    footer_text: 'Thank you for your business!',
    show_tax_column: true,
    show_discount_column: true
  });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(false);

  // Document Series state
  const [invoiceSeries, setInvoiceSeries] = useState({
    sales_invoice: { prefix: 'INV-', next_number: '', include_year: true },
    purchase_invoice: { prefix: 'PUR-', next_number: '', include_year: true },
    pos: { prefix: 'POS-', next_number: '', include_year: true },
    dbcr: { prefix: 'DBCR-', next_number: '', include_year: true },
    quotation: { prefix: 'QUO-', next_number: '', include_year: true }
  });
  const [seriesSaving, setSeriesSaving] = useState(false);

  // Fiscal Year state
  const [isFYDialogOpen, setIsFYDialogOpen] = useState(false);
  const [editingFY, setEditingFY] = useState(null);
  const [fyLoading, setFyLoading] = useState(false);
  const [closeConfirmFY, setCloseConfirmFY] = useState(null);
  const [closingFY, setClosingFY] = useState(false);
  const [closeResult, setCloseResult] = useState(null);
  const [deleteFYConfirm, setDeleteFYConfirm] = useState(null);
  const [newFY, setNewFY] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    organization_id: ''
  });

  // Role Permissions state
  // Keep in sync with navItems in Layout.jsx
  const ALL_MODULES = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/chart-of-accounts', label: 'Chart of Accounts' },
    { path: '/vouchers', label: 'Voucher Entry' },
    { path: '/cr-db-notes', label: 'Cr/Db Notes' },
    { path: '/sales-quotations', label: 'Sales Quotations' },
    { path: '/sales-invoices', label: 'Sales Invoices' },
    { path: '/sales-returns', label: 'Sales Returns' },
    { path: '/purchase-invoices', label: 'Purchase Invoices' },
    { path: '/purchase-orders', label: 'Purchase Orders' },
    { path: '/purchase-returns', label: 'Purchase Returns' },
    { path: '/customers', label: 'Customers' },
    { path: '/suppliers', label: 'Suppliers' },
    { path: '/inventory', label: 'Inventory' },
    { path: '/expiry-alerts', label: 'Expiry Alerts' },
    { path: '/physicians', label: 'Physicians' },
    { path: '/patients', label: 'Patients' },
    { path: '/prescription-register', label: 'Prescription Register' },
    { path: '/pos', label: 'POS Terminal' },
    { path: '/price-checker', label: 'Price Checker' },
    { path: '/faux-invoices', label: 'Faux Invoices' },
    { path: '/exchange-rates', label: 'Exchange Rates' },
    { path: '/trial-balance', label: 'Trial Balance' },
    { path: '/journal', label: 'Journal' },
    { path: '/income-statement', label: 'Income Statement' },
    { path: '/settings', label: 'Settings' },
  ];
  const ALL_ACTIONS = ['view', 'add', 'edit', 'delete', 'print', 'ai'];
  const ACTION_LABELS = { view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete', print: 'Print', ai: 'AI' };
  const CONFIGURABLE_ROLES = ['admin', 'accountant', 'cashier', 'viewer'];
  const [rolePermissions, setRolePermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [rolePermSaving, setRolePermSaving] = useState(false);

  // Default Accounts state
  const [defaultAccounts, setDefaultAccounts] = useState({
    sales_vat_account_id: '', sales_vat_account_code: '', sales_vat_account_name: '',
    purchase_vat_account_id: '', purchase_vat_account_code: '', purchase_vat_account_name: '',
    sales_account_id: '', sales_account_code: '', sales_account_name: '',
    purchase_account_id: '', purchase_account_code: '', purchase_account_name: '',
    cash_account_id: '', cash_account_code: '', cash_account_name: '',
    bank_account_id: '', bank_account_code: '', bank_account_name: '',
    pos_discount_premium_account_id: '', pos_discount_premium_account_code: '', pos_discount_premium_account_name: '',
    exchange_rounding_account_id: '', exchange_rounding_account_code: '', exchange_rounding_account_name: ''
  });
  
  // Trial Balance Fix state
  const [trialBalanceErrors, setTrialBalanceErrors] = useState([]);
  const [trialBalanceLoading, setTrialBalanceLoading] = useState(false);
  const [trialBalanceFixing, setTrialBalanceFixing] = useState(false);
  const [trialBalanceFixingAll, setTrialBalanceFixingAll] = useState(false);
  const [trialBalanceResult, setTrialBalanceResult] = useState(null);
  const [defaultAccountsSaving, setDefaultAccountsSaving] = useState(false);
  const [defaultAccountsLoading, setDefaultAccountsLoading] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountSearchResults, setAccountSearchResults] = useState([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [activeAccountField, setActiveAccountField] = useState(null);

  const [newOrg, setNewOrg] = useState({
    name: '',
    name_ar: '',
    tagline: '',
    logo_url: '',
    currency: 'LBP',
    base_exchange_rate: 89500,
    vat_registered: true,
    tax_percent: 11,
    tax_name: 'VAT',
    prices_include_tax: true,
    costs_include_tax: false,
    phone: '',
    email: '',
    address: '',
    address_ar: '',
    registration_number: '',
    enable_expiry_tracking: false,
    pos_quick_items_enabled: true,
    pos_keyboard_enabled: false,
    pos_show_barcode: true,
    pos_show_search: true,
    pos_show_customer: true
  });

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);

  const [newCurrency, setNewCurrency] = useState({
    code: '',
    name: '',
    symbol: '',
    rate_to_usd: 1,
    rate_to_lbp: 89500,
    is_active: true
  });

  // Barcode settings
  const [barcodePrefix, setBarcodePrefix] = useState('201007');
  const [barcodePrefixSaving, setBarcodePrefixSaving] = useState(false);
  
  // Claude API Key state (AI Integration)
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [savingClaudeKey, setSavingClaudeKey] = useState(false);
  const [testingClaudeApi, setTestingClaudeApi] = useState(false);
  const [claudeTestResult, setClaudeTestResult] = useState(null);
  const [barcodePrefixLoaded, setBarcodePrefixLoaded] = useState(false);

  // AWS/S3 Integration state
  const [awsAccessKeyId, setAwsAccessKeyId] = useState('');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState('');
  const [awsRegion, setAwsRegion] = useState('');
  const [s3BucketName, setS3BucketName] = useState('');
  const [savingAwsConfig, setSavingAwsConfig] = useState(false);

  // Emergent LLM Key state
  const [emergentLlmKey, setEmergentLlmKey] = useState('');
  const [savingEmergentKey, setSavingEmergentKey] = useState(false);

  // Image providers (OpenAI gpt-image-1, Unsplash, Pexels) keys
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [unsplashAccessKey, setUnsplashAccessKey] = useState('');
  const [pexelsApiKey, setPexelsApiKey] = useState('');
  const [savingImageKeys, setSavingImageKeys] = useState(false);

  // Bulk Delete Tools state
  const [bulkDeleteType, setBulkDeleteType] = useState('updated');
  const [bulkDeleteDate, setBulkDeleteDate] = useState('');
  const [bulkDeleteTime, setBulkDeleteTime] = useState('11:40');
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  // Branding & Version settings state
  const [brandingSettings, setBrandingSettings] = useState({
    version: '2.5',
    label: '',
    logo_url: null
  });
  const [brandingSaving, setBrandingSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCurrencies();
    fetchBackupInfo();
  }, [selectedOrgFilter]);

  // Fetch branding settings when org changes
  useEffect(() => {
    if (currentOrg?.id) {
      axios.get(`${API}/organizations/${currentOrg.id}/version-settings`)
        .then(res => {
          setBrandingSettings({
            version: res.data.version || '2.5',
            label: res.data.label || '',
            logo_url: res.data.logo_url || null
          });
        })
        .catch(() => {
          // Use defaults if not set
          setBrandingSettings({ version: '2.5', label: '', logo_url: null });
        });
    }
  }, [currentOrg?.id]);

  // Fetch barcode prefix when org changes
  useEffect(() => {
    if (currentOrg) {
      setBarcodePrefixLoaded(false);
      axios.get(`${API}/barcode-settings?organization_id=${currentOrg.id}`)
        .then(res => { setBarcodePrefix(res.data.prefix || '201007'); setBarcodePrefixLoaded(true); })
        .catch(() => setBarcodePrefixLoaded(true));
    }
  }, [currentOrg]);

  const saveBarcodePrefix = async () => {
    if (!currentOrg) return;
    if (!/^\d{6}$/.test(barcodePrefix)) { alert('Prefix must be exactly 6 digits'); return; }
    setBarcodePrefixSaving(true);
    try {
      await axios.put(`${API}/barcode-settings`, { organization_id: currentOrg.id, prefix: barcodePrefix });
      alert('Barcode prefix saved successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setBarcodePrefixSaving(false); }
  };

  // Load invoice template when currentOrg changes
  useEffect(() => {
    if (currentOrg?.invoice_template) {
      setInvoiceTemplate({
        company_name: currentOrg.invoice_template.company_name || currentOrg.name || '',
        company_type: currentOrg.invoice_template.company_type || 'S.A.R.L.',
        tel_fax: currentOrg.invoice_template.tel_fax || '',
        mobile: currentOrg.invoice_template.mobile || '',
        email: currentOrg.invoice_template.email || '',
        address: currentOrg.invoice_template.address || '',
        logo_url: currentOrg.invoice_template.logo_url || '',
        footer_text: currentOrg.invoice_template.footer_text || 'Thank you for your business!',
        show_tax_column: currentOrg.invoice_template.show_tax_column ?? true,
        show_discount_column: currentOrg.invoice_template.show_discount_column ?? true
      });
    } else if (currentOrg) {
      setInvoiceTemplate({
        company_name: currentOrg.name || '',
        company_type: 'S.A.R.L.',
        tel_fax: '',
        mobile: '',
        email: '',
        address: '',
        logo_url: '',
        footer_text: 'Thank you for your business!',
        show_tax_column: true,
        show_discount_column: true
      });
    }
  }, [currentOrg]);

  // Load invoice series when currentOrg changes
  useEffect(() => {
    // Load role permissions from org
    if (currentOrg?.role_permissions) {
      setRolePermissions(currentOrg.role_permissions);
    } else {
      // Default: all modules ON for all roles
      const defaults = {};
      CONFIGURABLE_ROLES.forEach(role => {
        defaults[role] = ALL_MODULES.map(m => m.path);
      });
      setRolePermissions(defaults);
    }
    if (currentOrg?.invoice_series) {
      setInvoiceSeries({
        sales_invoice: {
          prefix: currentOrg.invoice_series.sales_invoice?.prefix || 'INV-',
          next_number: currentOrg.invoice_series.sales_invoice?.next_number || '',
          include_year: currentOrg.invoice_series.sales_invoice?.include_year ?? true
        },
        purchase_invoice: {
          prefix: currentOrg.invoice_series.purchase_invoice?.prefix || 'PUR-',
          next_number: currentOrg.invoice_series.purchase_invoice?.next_number || '',
          include_year: currentOrg.invoice_series.purchase_invoice?.include_year ?? true
        },
        pos: {
          prefix: currentOrg.invoice_series.pos?.prefix || 'POS-',
          next_number: currentOrg.invoice_series.pos?.next_number || '',
          include_year: currentOrg.invoice_series.pos?.include_year ?? true
        },
        dbcr: {
          prefix: currentOrg.invoice_series.dbcr?.prefix || 'DBCR-',
          next_number: currentOrg.invoice_series.dbcr?.next_number || '',
          include_year: currentOrg.invoice_series.dbcr?.include_year ?? true
        },
        quotation: {
          prefix: currentOrg.invoice_series.quotation?.prefix || 'QUO-',
          next_number: currentOrg.invoice_series.quotation?.next_number || '',
          include_year: currentOrg.invoice_series.quotation?.include_year ?? true
        }
      });
    } else {
      setInvoiceSeries({
        sales_invoice: { prefix: 'INV-', next_number: '', include_year: true },
        purchase_invoice: { prefix: 'PUR-', next_number: '', include_year: true },
        pos: { prefix: 'POS-', next_number: '', include_year: true },
        dbcr: { prefix: 'DBCR-', next_number: '', include_year: true },
        quotation: { prefix: 'QUO-', next_number: '', include_year: true }
      });
    }
  }, [currentOrg]);

  const fetchBackupInfo = async () => {
    try {
      const response = await axios.get(`${API}/backup/info`);
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch backup info:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = selectedOrgFilter === 'all' 
        ? `${API}/users`
        : `${API}/users?organization_id=${selectedOrgFilter}`;
      const response = await axios.get(url);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    }
  };

  // Fetch Default Accounts
  const fetchDefaultAccounts = async () => {
    if (!currentOrg) return;
    setDefaultAccountsLoading(true);
    try {
      const response = await axios.get(`${API}/organizations/${currentOrg.id}/default-accounts`);
      setDefaultAccounts(response.data || {});
    } catch (error) {
      console.error('Failed to fetch default accounts:', error);
    } finally {
      setDefaultAccountsLoading(false);
    }
  };

  // Search accounts for dropdown
  const searchAccounts = async (query) => {
    if (!currentOrg || !query || query.length < 1) {
      setAccountSearchResults([]);
      return;
    }
    setSearchingAccounts(true);
    try {
      const response = await axios.get(`${API}/accounts?organization_id=${currentOrg.id}&search=${encodeURIComponent(query)}&limit=20`);
      // Handle both array response and {accounts: [...]} response format
      const accounts = response.data?.accounts || response.data || [];
      setAccountSearchResults(accounts);
    } catch (error) {
      console.error('Failed to search accounts:', error);
    } finally {
      setSearchingAccounts(false);
    }
  };

  // Save Default Accounts
  const saveDefaultAccounts = async () => {
    if (!currentOrg) return;
    setDefaultAccountsSaving(true);
    try {
      await axios.put(`${API}/organizations/${currentOrg.id}/default-accounts`, defaultAccounts);
      alert('Default accounts saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save default accounts');
    } finally {
      setDefaultAccountsSaving(false);
    }
  };

  // Handle account selection for a field
  const handleSelectAccount = (fieldPrefix, account) => {
    setDefaultAccounts(prev => ({
      ...prev,
      [`${fieldPrefix}_account_id`]: account.id,
      [`${fieldPrefix}_account_code`]: account.code,
      [`${fieldPrefix}_account_name`]: account.name
    }));
    setActiveAccountField(null);
    setAccountSearch('');
    setAccountSearchResults([]);
  };

  // Clear account for a field
  const handleClearAccount = (fieldPrefix) => {
    setDefaultAccounts(prev => ({
      ...prev,
      [`${fieldPrefix}_account_id`]: '',
      [`${fieldPrefix}_account_code`]: '',
      [`${fieldPrefix}_account_name`]: ''
    }));
  };

  // Trial Balance Fix handlers
  const scanTrialBalanceErrors = async () => {
    if (!currentOrg?.id) return;
    setTrialBalanceLoading(true);
    setTrialBalanceErrors([]);
    setTrialBalanceResult(null);
    try {
      const response = await axios.get(`${API}/settings/trial-balance-errors?organization_id=${currentOrg.id}`);
      setTrialBalanceErrors(response.data.vouchers || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to scan vouchers');
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  const fixSingleVoucher = async (voucherId) => {
    if (!currentOrg?.id) return;
    setTrialBalanceFixing(voucherId);
    try {
      const response = await axios.post(`${API}/settings/fix-trial-balance-error/${voucherId}?organization_id=${currentOrg.id}`);
      setTrialBalanceResult({ success: true, message: response.data.message, voucher_number: response.data.voucher_number });
      // Remove fixed voucher from list
      setTrialBalanceErrors(prev => prev.filter(v => v.id !== voucherId));
    } catch (err) {
      setTrialBalanceResult({ success: false, message: err.response?.data?.detail || 'Failed to fix voucher' });
    } finally {
      setTrialBalanceFixing(false);
    }
  };

  const fixAllVouchers = async () => {
    if (!currentOrg?.id || trialBalanceErrors.length === 0) return;
    if (!window.confirm(`Fix all ${trialBalanceErrors.length} unbalanced vouchers? This will add adjustment entries to each.`)) return;
    
    setTrialBalanceFixingAll(true);
    setTrialBalanceResult(null);
    try {
      const response = await axios.post(`${API}/settings/fix-all-trial-balance-errors?organization_id=${currentOrg.id}`);
      setTrialBalanceResult({ 
        success: true, 
        message: response.data.message,
        fixed_count: response.data.fixed_count,
        errors: response.data.errors
      });
      // Clear list and rescan
      setTrialBalanceErrors([]);
      await scanTrialBalanceErrors();
    } catch (err) {
      setTrialBalanceResult({ success: false, message: err.response?.data?.detail || 'Failed to fix vouchers' });
    } finally {
      setTrialBalanceFixingAll(false);
    }
  };

  // Orphan Accounts Scan handlers
  const [orphanAccounts, setOrphanAccounts] = useState([]);
  const [orphanAccountsLoading, setOrphanAccountsLoading] = useState(false);
  const [orphanAccountsStats, setOrphanAccountsStats] = useState(null);

  const scanOrphanAccounts = async () => {
    if (!currentOrg?.id) return;
    setOrphanAccountsLoading(true);
    setOrphanAccounts([]);
    setOrphanAccountsStats(null);
    try {
      const response = await axios.get(`${API}/settings/orphan-accounts-scan?organization_id=${currentOrg.id}`);
      setOrphanAccounts(response.data.missing_accounts || []);
      setOrphanAccountsStats({
        total_vouchers: response.data.total_vouchers_scanned,
        total_accounts: response.data.total_accounts_in_coa
      });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to scan accounts');
    } finally {
      setOrphanAccountsLoading(false);
    }
  };

  // Account Balance Reconciliation handlers
  const [balanceDiscrepancies, setBalanceDiscrepancies] = useState([]);
  const [balanceReconcileLoading, setBalanceReconcileLoading] = useState(false);
  const [balanceReconcileStats, setBalanceReconcileStats] = useState(null);
  const [balanceReconcileFixing, setBalanceReconcileFixing] = useState(false);
  const [balanceReconcileFixingAll, setBalanceReconcileFixingAll] = useState(false);

  const scanBalanceDiscrepancies = async () => {
    if (!currentOrg?.id) return;
    setBalanceReconcileLoading(true);
    setBalanceDiscrepancies([]);
    setBalanceReconcileStats(null);
    try {
      const response = await axios.get(`${API}/settings/account-balance-reconciliation?organization_id=${currentOrg.id}`);
      setBalanceDiscrepancies(response.data.discrepancies || []);
      setBalanceReconcileStats({
        total_accounts: response.data.total_accounts,
        voucher_totals: response.data.voucher_totals
      });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to scan balances');
    } finally {
      setBalanceReconcileLoading(false);
    }
  };

  const recalculateSingleBalance = async (accountId) => {
    if (!currentOrg?.id) return;
    setBalanceReconcileFixing(accountId);
    try {
      await axios.post(`${API}/settings/recalculate-account-balance/${accountId}?organization_id=${currentOrg.id}`);
      // Remove fixed account from list
      setBalanceDiscrepancies(prev => prev.filter(d => d.account_id !== accountId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to recalculate balance');
    } finally {
      setBalanceReconcileFixing(false);
    }
  };

  const recalculateAllBalances = async () => {
    if (!currentOrg?.id) return;
    if (!window.confirm('This will recalculate ALL account balances from voucher entries. Continue?')) return;
    setBalanceReconcileFixingAll(true);
    try {
      await axios.post(`${API}/settings/recalculate-all-account-balances?organization_id=${currentOrg.id}`);
      setBalanceDiscrepancies([]);
      await scanBalanceDiscrepancies();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to recalculate balances');
    } finally {
      setBalanceReconcileFixingAll(false);
    }
  };

  // Account Transfer handlers
  const [transferSourceCode, setTransferSourceCode] = useState('');
  const [transferTargetCode, setTransferTargetCode] = useState('');
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferPreviewLoading, setTransferPreviewLoading] = useState(false);
  const [transferExecuting, setTransferExecuting] = useState(false);

  const previewAccountTransfer = async () => {
    if (!currentOrg?.id || !transferSourceCode || !transferTargetCode) return;
    setTransferPreviewLoading(true);
    setTransferPreview(null);
    try {
      const response = await axios.get(
        `${API}/settings/preview-account-transfer?organization_id=${currentOrg.id}&source_account_code=${transferSourceCode}&target_account_code=${transferTargetCode}`
      );
      setTransferPreview(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to preview transfer');
    } finally {
      setTransferPreviewLoading(false);
    }
  };

  const executeAccountTransfer = async () => {
    if (!currentOrg?.id || !transferSourceCode || !transferTargetCode || !transferPreview) return;
    if (!window.confirm(`Transfer ${transferPreview.impact.lines_affected} transactions from ${transferSourceCode} to ${transferTargetCode}? This cannot be undone!`)) return;
    setTransferExecuting(true);
    try {
      const response = await axios.post(
        `${API}/settings/transfer-account-transactions?organization_id=${currentOrg.id}&source_account_code=${transferSourceCode}&target_account_code=${transferTargetCode}`
      );
      alert(response.data.message);
      setTransferPreview(null);
      setTransferSourceCode('');
      setTransferTargetCode('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to execute transfer');
    } finally {
      setTransferExecuting(false);
    }
  };

  // Exchange Rate Validation handlers
  const [exchangeRateErrors, setExchangeRateErrors] = useState([]);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateStats, setExchangeRateStats] = useState(null);
  const [exchangeRateValidRates, setExchangeRateValidRates] = useState('89500,8900');
  const [exchangeRateTolerance, setExchangeRateTolerance] = useState('750');
  const [exchangeRateFixing, setExchangeRateFixing] = useState(false);
  const [exchangeRateFixingAll, setExchangeRateFixingAll] = useState(false);
  const [exchangeRateCorrectRate, setExchangeRateCorrectRate] = useState('89500');

  const scanExchangeRateErrors = async () => {
    if (!currentOrg?.id) return;
    setExchangeRateLoading(true);
    setExchangeRateErrors([]);
    setExchangeRateStats(null);
    try {
      const response = await axios.get(
        `${API}/settings/scan-exchange-rate-errors?organization_id=${currentOrg.id}&valid_rates=${exchangeRateValidRates}&tolerance=${exchangeRateTolerance}`
      );
      setExchangeRateErrors(response.data.errors || []);
      setExchangeRateStats({
        total_vouchers: response.data.total_vouchers_scanned,
        has_more: response.data.has_more
      });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to scan exchange rates');
    } finally {
      setExchangeRateLoading(false);
    }
  };

  const fixSingleExchangeRate = async (voucherId, lineIndex, nearestRate) => {
    if (!currentOrg?.id) return;
    setExchangeRateFixing(`${voucherId}-${lineIndex}`);
    try {
      await axios.post(
        `${API}/settings/fix-exchange-rate-error?organization_id=${currentOrg.id}&voucher_id=${voucherId}&line_index=${lineIndex}&correct_rate=${nearestRate}`
      );
      // Remove fixed error from list
      setExchangeRateErrors(prev => prev.filter(e => !(e.voucher_id === voucherId && e.line_index === lineIndex)));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to fix exchange rate');
    } finally {
      setExchangeRateFixing(false);
    }
  };

  const fixAllExchangeRates = async () => {
    if (!currentOrg?.id || exchangeRateErrors.length === 0) return;
    if (!window.confirm(`Fix all ${exchangeRateErrors.length} exchange rate errors using rate ${exchangeRateCorrectRate}? This cannot be undone!`)) return;
    setExchangeRateFixingAll(true);
    try {
      const response = await axios.post(
        `${API}/settings/fix-all-exchange-rate-errors?organization_id=${currentOrg.id}&correct_rate=${exchangeRateCorrectRate}&valid_rates=${exchangeRateValidRates}&tolerance=${exchangeRateTolerance}`
      );
      alert(response.data.message);
      setExchangeRateErrors([]);
      await scanExchangeRateErrors();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to fix exchange rates');
    } finally {
      setExchangeRateFixingAll(false);
    }
  };

  // Account Audit handlers
  const [auditAccountCode, setAuditAccountCode] = useState('');
  const [auditExpectedRate, setAuditExpectedRate] = useState('89500');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditShowAll, setAuditShowAll] = useState(false);

  const auditAccount = async () => {
    if (!currentOrg?.id || !auditAccountCode) return;
    setAuditLoading(true);
    setAuditResult(null);
    setAuditShowAll(false);
    try {
      const response = await axios.get(
        `${API}/settings/audit-account-transactions?organization_id=${currentOrg.id}&account_code=${auditAccountCode}&expected_rate=${auditExpectedRate}`
      );
      setAuditResult(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to audit account');
    } finally {
      setAuditLoading(false);
    }
  };

  // Fetch default accounts when org changes
  useEffect(() => {
    if (currentOrg) {
      fetchDefaultAccounts();
    }
  }, [currentOrg?.id]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: newUser.name,
          role: newUser.role,
          organization_id: newUser.organization_id || null
        });
      } else {
        await axios.post(`${API}/users`, newUser);
      }
      setIsUserDialogOpen(false);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await axios.put(`${API}/organizations/${editingOrg.id}`, newOrg);
      } else {
        await axios.post(`${API}/organizations`, newOrg);
      }
      setIsOrgDialogOpen(false);
      resetOrgForm();
      fetchOrganizations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save organization');
    }
  };

  const handleCreateCurrency = async (e) => {
    e.preventDefault();
    try {
      if (editingCurrency) {
        await axios.put(`${API}/currencies/${editingCurrency.id}`, {
          name: newCurrency.name,
          symbol: newCurrency.symbol,
          rate_to_usd: newCurrency.rate_to_usd,
          rate_to_lbp: newCurrency.rate_to_lbp,
          is_active: newCurrency.is_active
        });
      } else {
        await axios.post(`${API}/currencies`, newCurrency);
      }
      setIsCurrencyDialogOpen(false);
      resetCurrencyForm();
      fetchCurrencies();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save currency');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/users/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteOrg = async (forceDelete = false) => {
    if (!deleteOrgConfirm) return;
    try {
      const url = forceDelete 
        ? `${API}/organizations/${deleteOrgConfirm.id}?force=true`
        : `${API}/organizations/${deleteOrgConfirm.id}`;
      const response = await axios.delete(url);
      alert(response.data.message);
      setDeleteOrgConfirm(null);
      setDeleteOrgForce(false);
      setDeleteOrgError(null);
      fetchOrganizations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete organization';
      // Check if error indicates data exists
      if (errorMsg.includes('has data:') || errorMsg.includes('user(s)') || errorMsg.includes('account(s)')) {
        setDeleteOrgError(errorMsg);
        setDeleteOrgForce(true);
      } else {
        alert(errorMsg);
      }
    }
  };

  const handleDeleteCurrency = async () => {
    if (!deleteCurrencyConfirm) return;
    try {
      await axios.delete(`${API}/currencies/${deleteCurrencyConfirm.id}`);
      setDeleteCurrencyConfirm(null);
      fetchCurrencies();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete currency');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    try {
      await axios.post(`${API}/users/${resetPasswordUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      setResetPasswordUser(null);
      setNewPassword('');
      alert('Password reset successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { is_active: !u.is_active });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleCurrencyActive = async (c) => {
    try {
      await axios.put(`${API}/currencies/${c.id}`, { is_active: !c.is_active });
      fetchCurrencies();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update currency');
    }
  };

  const handleSeedCurrencies = async () => {
    setSeedingCurrencies(true);
    try {
      await axios.post(`${API}/currencies/seed`);
      fetchCurrencies();
      alert('Currencies seeded successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to seed currencies');
    } finally {
      setSeedingCurrencies(false);
    }
  };

  // Backup & Restore handlers
  const handleCreateBackup = async (orgId = null) => {
    setBackupLoading(true);
    try {
      const url = orgId ? `${API}/backup?organization_id=${orgId}` : `${API}/backup`;
      const response = await axios.post(url);
      
      // Download the backup file
      const backupData = response.data.data;
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = response.data.filename;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      alert(`Backup created successfully!\nFile: ${response.data.filename}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.json')) {
      setRestoreFile(file);
      setRestoreConfirmOpen(true);
    } else {
      alert('Please select a valid JSON backup file');
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) return;
    
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      
      const response = await axios.post(
        `${API}/restore?mode=${restoreMode}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setRestoreResult(response.data);
      
      // Refresh data
      fetchUsers();
      fetchCurrencies();
      fetchOrganizations();
      fetchBackupInfo();
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoreLoading(false);
      setRestoreFile(null);
      if (restoreFileRef.current) restoreFileRef.current.value = '';
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setNewUser({
      email: u.email,
      password: '',
      name: u.name,
      role: u.role,
      organization_id: u.organization_id || ''
    });
    setIsUserDialogOpen(true);
  };

  const handleEditOrg = (org) => {
    setEditingOrg(org);
    setNewOrg({
      name: org.name,
      name_ar: org.name_ar || '',
      tagline: org.tagline || '',
      logo_url: org.logo_url || '',
      currency: org.currency,
      base_exchange_rate: org.base_exchange_rate,
      vat_registered: org.vat_registered !== false, // Default to true
      tax_percent: org.tax_percent !== undefined ? org.tax_percent : 11,
      tax_name: org.tax_name || 'VAT',
      prices_include_tax: org.prices_include_tax !== false, // Default true
      costs_include_tax: org.costs_include_tax === true, // Default false
      phone: org.phone || '',
      email: org.email || '',
      address: org.address || '',
      address_ar: org.address_ar || '',
      registration_number: org.registration_number || '',
      enable_expiry_tracking: org.enable_expiry_tracking || false,
      pos_quick_items_enabled: org.pos_quick_items_enabled !== false,
      pos_keyboard_enabled: org.pos_keyboard_enabled === true,
      pos_show_barcode: org.pos_show_barcode !== false,
      pos_show_search: org.pos_show_search !== false,
      pos_show_customer: org.pos_show_customer !== false
    });
    setIsOrgDialogOpen(true);
  };

  // Handle Organization logo upload to S3
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingOrg) return;
    if (file.size > 4 * 1024 * 1024) {
      alert('Logo must be < 4 MB');
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organization_id', editingOrg.id);
      const resp = await axios.post(`${API}/files/organization-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = resp?.data?.url || resp?.data?.file?.url;
      if (url) {
        setNewOrg((s) => ({ ...s, logo_url: url }));
        // Persist immediately so reload picks it up
        try {
          await axios.put(`${API}/organizations/${editingOrg.id}`, { logo_url: url });
          await fetchOrganizations?.();
        } catch (_) {}
      }
    } catch (err) {
      console.error('Logo upload failed', err);
      alert(`Logo upload failed: ${err?.response?.data?.detail || err.message}. Make sure AWS S3 is configured in the Integrations tab.`);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!editingOrg) return;
    setNewOrg((s) => ({ ...s, logo_url: '' }));
    try {
      await axios.put(`${API}/organizations/${editingOrg.id}`, { logo_url: null });
      await fetchOrganizations?.();
    } catch (_) {}
  };

  // Save Invoice Template
  const handleSaveTemplate = async () => {
    if (!currentOrg) {
      alert('Please select an organization first');
      return;
    }
    
    setTemplateSaving(true);
    try {
      await axios.put(`${API}/organizations/${currentOrg.id}`, {
        invoice_template: invoiceTemplate
      });
      // Refresh organizations to get updated data
      await fetchOrganizations();
      alert('Invoice template saved successfully!');
    } catch (error) {
      console.error('Failed to save template:', error);
      alert(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleSaveInvoiceSeries = async () => {
    if (!currentOrg) {
      alert('Please select an organization first');
      return;
    }
    
    setSeriesSaving(true);
    try {
      // Clean up the series data - convert next_number to integer or null
      const cleanedSeries = {};
      Object.keys(invoiceSeries).forEach(key => {
        cleanedSeries[key] = {
          prefix: invoiceSeries[key].prefix || '',
          next_number: invoiceSeries[key].next_number ? parseInt(invoiceSeries[key].next_number, 10) : null,
          include_year: invoiceSeries[key].include_year
        };
      });
      
      await axios.put(`${API}/organizations/${currentOrg.id}`, {
        invoice_series: cleanedSeries
      });
      // Refresh organizations to get updated data
      await fetchOrganizations();
      alert('Document series settings saved successfully!');
    } catch (error) {
      console.error('Failed to save series settings:', error);
      alert(error.response?.data?.detail || 'Failed to save series settings');
    } finally {
      setSeriesSaving(false);
    }
  };

  // Claude API Key handlers
  const handleSaveClaudeApiKey = async () => {
    if (!currentOrg || !claudeApiKey.trim()) return;
    
    setSavingClaudeKey(true);
    setClaudeTestResult(null);
    try {
      await axios.put(`${API}/organizations/${currentOrg.id}`, {
        claude_api_key: claudeApiKey.trim()
      });
      await fetchOrganizations();
      setClaudeApiKey(''); // Clear the input after saving
      alert('Claude API key saved successfully!');
    } catch (error) {
      console.error('Failed to save Claude API key:', error);
      alert(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSavingClaudeKey(false);
    }
  };

  const handleTestClaudeApi = async () => {
    if (!currentOrg?.has_claude_api_key) return;
    
    setTestingClaudeApi(true);
    setClaudeTestResult(null);
    try {
      // Verify the key is configured server-side
      const response = await axios.get(`${API}/ai-status`);
      
      if (response.data?.key_found) {
        setClaudeTestResult({
          success: true,
          message: '✓ AI key detected on server.'
        });
      } else {
        setClaudeTestResult({
          success: false,
          message: '✗ No AI key found on server. Please save it first.'
        });
      }
    } catch (error) {
      console.error('AI status check failed:', error);
      setClaudeTestResult({
        success: false,
        message: `✗ Test failed: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setTestingClaudeApi(false);
    }
  };

  // AWS/S3 Integration handlers
  const handleSaveAwsConfig = async () => {
    if (!currentOrg) return;
    if (!awsAccessKeyId.trim() && !awsSecretAccessKey.trim() && !awsRegion.trim() && !s3BucketName.trim()) {
      alert('Please enter at least one field to update');
      return;
    }
    
    setSavingAwsConfig(true);
    try {
      const updateData = {};
      if (awsAccessKeyId.trim()) updateData.aws_access_key_id = awsAccessKeyId.trim();
      if (awsSecretAccessKey.trim()) updateData.aws_secret_access_key = awsSecretAccessKey.trim();
      if (awsRegion.trim()) updateData.aws_region = awsRegion.trim();
      if (s3BucketName.trim()) updateData.s3_bucket_name = s3BucketName.trim();
      
      await axios.put(`${API}/organizations/${currentOrg.id}`, updateData);
      await fetchOrganizations();
      // Clear sensitive fields after saving
      setAwsAccessKeyId('');
      setAwsSecretAccessKey('');
      alert('AWS/S3 configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save AWS config:', error);
      alert(error.response?.data?.detail || 'Failed to save AWS configuration');
    } finally {
      setSavingAwsConfig(false);
    }
  };

  // Emergent LLM Key handler
  const handleSaveEmergentLlmKey = async () => {
    if (!currentOrg || !emergentLlmKey.trim()) return;
    
    setSavingEmergentKey(true);
    try {
      await axios.put(`${API}/organizations/${currentOrg.id}`, {
        emergent_llm_key: emergentLlmKey.trim()
      });
      await fetchOrganizations();
      setEmergentLlmKey(''); // Clear the input after saving
      alert('Emergent LLM key saved successfully!');
    } catch (error) {
      console.error('Failed to save Emergent LLM key:', error);
      alert(error.response?.data?.detail || 'Failed to save Emergent LLM key');
    } finally {
      setSavingEmergentKey(false);
    }
  };

  const handleEditCurrency = (c) => {
    setEditingCurrency(c);
    setNewCurrency({
      code: c.code,
      name: c.name,
      symbol: c.symbol,
      rate_to_usd: c.rate_to_usd,
      rate_to_lbp: c.rate_to_lbp,
      is_active: c.is_active
    });
    setIsCurrencyDialogOpen(true);
  };

  const resetUserForm = () => {
    setNewUser({
      email: '',
      password: '',
      name: '',
      role: 'viewer',
      organization_id: ''
    });
    setEditingUser(null);
  };

  const resetOrgForm = () => {
    setNewOrg({
      name: '',
      currency: 'LBP',
      base_exchange_rate: 89500,
      tax_percent: 11,
      tax_name: 'VAT',
      prices_include_tax: true,
      costs_include_tax: false,
      phone: '',
      email: '',
      address: '',
      registration_number: '',
      enable_expiry_tracking: false,
      pos_quick_items_enabled: true,
      pos_keyboard_enabled: false,
      pos_show_barcode: true,
      pos_show_search: true,
      pos_show_customer: true
    });
    setEditingOrg(null);
  };

  const resetCurrencyForm = () => {
    setNewCurrency({
      code: '',
      name: '',
      symbol: '',
      rate_to_usd: 1,
      rate_to_lbp: 89500,
      is_active: true
    });
    setEditingCurrency(null);
  };

  const roleColors = {
    super_admin: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    accountant: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    cashier: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    viewer: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
  };

  // Role Permissions handlers - Action-level permissions
  // Permission structure: { role: { modulePath: ['view', 'add', 'edit', ...] } }
  
  const getModuleActions = (role, modulePath) => {
    const rolePerms = rolePermissions[role];
    if (!rolePerms) return ALL_ACTIONS; // Default to all actions for new roles
    const modulePerms = rolePerms[modulePath];
    if (Array.isArray(modulePerms)) return modulePerms;
    // Legacy support: if modulePath is in array, grant all actions
    if (Array.isArray(rolePerms) && rolePerms.includes(modulePath)) return ALL_ACTIONS;
    return [];
  };

  const hasModuleAccess = (role, modulePath) => {
    const actions = getModuleActions(role, modulePath);
    return actions.length > 0;
  };

  const hasActionPermission = (role, modulePath, action) => {
    const actions = getModuleActions(role, modulePath);
    return actions.includes(action);
  };

  const toggleRoleAction = (role, modulePath, action) => {
    setRolePermissions(prev => {
      const rolePerms = prev[role] || {};
      let currentActions = [];
      
      // Handle legacy format (array of paths)
      if (Array.isArray(rolePerms)) {
        // Convert legacy format to new format
        const newRolePerms = {};
        rolePerms.forEach(path => {
          newRolePerms[path] = [...ALL_ACTIONS];
        });
        currentActions = newRolePerms[modulePath] || [];
        return {
          ...prev,
          [role]: {
            ...newRolePerms,
            [modulePath]: currentActions.includes(action) 
              ? currentActions.filter(a => a !== action)
              : [...currentActions, action]
          }
        };
      }
      
      currentActions = rolePerms[modulePath] || [];
      const hasAction = currentActions.includes(action);
      
      return {
        ...prev,
        [role]: {
          ...rolePerms,
          [modulePath]: hasAction 
            ? currentActions.filter(a => a !== action)
            : [...currentActions, action]
        }
      };
    });
  };

  const toggleRoleModule = (role, modulePath) => {
    setRolePermissions(prev => {
      const rolePerms = prev[role] || {};
      
      // Handle legacy format
      if (Array.isArray(rolePerms)) {
        const has = rolePerms.includes(modulePath);
        const newRolePerms = {};
        rolePerms.forEach(path => {
          newRolePerms[path] = [...ALL_ACTIONS];
        });
        if (has) {
          delete newRolePerms[modulePath];
        } else {
          newRolePerms[modulePath] = [...ALL_ACTIONS];
        }
        return { ...prev, [role]: newRolePerms };
      }
      
      const currentActions = rolePerms[modulePath] || [];
      const hasAccess = currentActions.length > 0;
      
      return {
        ...prev,
        [role]: {
          ...rolePerms,
          [modulePath]: hasAccess ? [] : [...ALL_ACTIONS]
        }
      };
    });
  };

  const toggleAllForRole = (role, on) => {
    setRolePermissions(prev => {
      const newPerms = {};
      ALL_MODULES.forEach(m => {
        newPerms[m.path] = on ? [...ALL_ACTIONS] : [];
      });
      return { ...prev, [role]: newPerms };
    });
  };

  const toggleAllActionsForModule = (role, modulePath, on) => {
    setRolePermissions(prev => {
      const rolePerms = prev[role] || {};
      
      // Handle legacy format
      if (Array.isArray(rolePerms)) {
        const newRolePerms = {};
        rolePerms.forEach(path => {
          newRolePerms[path] = [...ALL_ACTIONS];
        });
        newRolePerms[modulePath] = on ? [...ALL_ACTIONS] : [];
        return { ...prev, [role]: newRolePerms };
      }
      
      return {
        ...prev,
        [role]: {
          ...rolePerms,
          [modulePath]: on ? [...ALL_ACTIONS] : []
        }
      };
    });
  };

  const toggleExpandModule = (modulePath) => {
    setExpandedModules(prev => ({
      ...prev,
      [modulePath]: !prev[modulePath]
    }));
  };

  const saveRolePermissions = async () => {
    setRolePermSaving(true);
    try {
      await axios.put(`${API}/organizations/${currentOrg.id}`, { role_permissions: rolePermissions });
      alert('Role permissions saved successfully!');
      await fetchOrganizations();
    } catch (err) {
      alert('Failed to save permissions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRolePermSaving(false);
    }
  };

  // =================== FISCAL YEAR HANDLERS ===================
  const handleCreateFY = async (e) => {
    e.preventDefault();
    if (!currentOrg) return;
    setFyLoading(true);
    try {
      await axios.post(`${API}/fiscal-years`, {
        ...newFY,
        organization_id: currentOrg.id
      });
      setNewFY({ name: '', start_date: '', end_date: '' });
      setIsFYDialogOpen(false);
      setEditingFY(null);
      fetchFiscalYears();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create fiscal year');
    } finally {
      setFyLoading(false);
    }
  };

  const handleUpdateFY = async (e) => {
    e.preventDefault();
    if (!editingFY) return;
    setFyLoading(true);
    try {
      await axios.put(`${API}/fiscal-years/${editingFY.id}`, {
        name: newFY.name,
        start_date: newFY.start_date,
        end_date: newFY.end_date
      });
      setNewFY({ name: '', start_date: '', end_date: '' });
      setIsFYDialogOpen(false);
      setEditingFY(null);
      fetchFiscalYears();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update fiscal year');
    } finally {
      setFyLoading(false);
    }
  };

  const handleDeleteFY = async () => {
    if (!deleteFYConfirm) return;
    try {
      await axios.delete(`${API}/fiscal-years/${deleteFYConfirm.id}`);
      setDeleteFYConfirm(null);
      fetchFiscalYears();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete fiscal year');
    }
  };

  const handleCloseFY = async () => {
    if (!closeConfirmFY) return;
    setClosingFY(true);
    try {
      const response = await axios.post(`${API}/fiscal-years/${closeConfirmFY.id}/close`);
      setCloseResult(response.data);
      fetchFiscalYears();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to close fiscal year');
    } finally {
      setClosingFY(false);
    }
  };

  const handleReopenFY = async (fy) => {
    if (!window.confirm(`Are you sure you want to reopen fiscal year "${fy.name}"? This will reverse closing entries.`)) return;
    try {
      await axios.post(`${API}/fiscal-years/${fy.id}/reopen`);
      fetchFiscalYears();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reopen fiscal year');
    }
  };

  const openEditFY = (fy) => {
    setEditingFY(fy);
    setNewFY({
      name: fy.name,
      start_date: fy.start_date,
      end_date: fy.end_date
    });
    setIsFYDialogOpen(true);
  };

  const openCreateFY = () => {
    setEditingFY(null);
    setNewFY({ name: '', start_date: '', end_date: '' });
    setIsFYDialogOpen(true);
  };

  // Bulk Delete Tools handlers
  const handleBulkDeletePreview = async () => {
    if (!bulkDeleteDate || !bulkDeleteTime || !currentOrg) return;
    
    setBulkDeleteLoading(true);
    setBulkDeletePreview(null);
    setBulkDeleteResult(null);
    
    try {
      const afterDatetime = `${bulkDeleteDate}T${bulkDeleteTime}:00`;
      const response = await axios.post(`${API}/inventory/bulk-delete/preview`, {
        organization_id: currentOrg.id,
        after_datetime: afterDatetime,
        delete_type: bulkDeleteType
      });
      setBulkDeletePreview(response.data);
    } catch (err) {
      setBulkDeleteResult({ success: false, error: err.response?.data?.detail || 'Preview failed' });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleBulkDeleteExecute = async () => {
    if (!bulkDeletePreview || !currentOrg) return;
    
    setBulkDeleteLoading(true);
    setBulkDeleteConfirmOpen(false);
    
    try {
      const response = await axios.post(`${API}/inventory/bulk-delete/execute`, {
        organization_id: currentOrg.id,
        after_datetime: bulkDeletePreview.after_datetime,
        delete_type: bulkDeleteType,
        confirm: true
      });
      setBulkDeleteResult(response.data);
      setBulkDeletePreview(null);
    } catch (err) {
      setBulkDeleteResult({ success: false, error: err.response?.data?.detail || 'Delete failed' });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Access denied. Super Admin only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-muted-foreground text-xs lg:text-sm mt-1">
          Manage users, roles, organizations, currencies, and backups
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="grid w-full grid-cols-8 lg:w-[1050px]">
          <TabsTrigger value="users" className="text-xs lg:text-sm">
            <Users className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="organizations" className="text-xs lg:text-sm">
            <Building2 className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Organizations</span>
          </TabsTrigger>
          <TabsTrigger value="fiscal-years" className="text-xs lg:text-sm" data-testid="tab-fiscal-years">
            <Calendar className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Fiscal Years</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="text-xs lg:text-sm">
            <Package className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Services</span>
          </TabsTrigger>
          <TabsTrigger value="invoice-template" className="text-xs lg:text-sm">
            <FileText className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Invoice Template</span>
          </TabsTrigger>
          <TabsTrigger value="document-series" className="text-xs lg:text-sm">
            <Hash className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Document Series</span>
          </TabsTrigger>
          <TabsTrigger value="currencies" className="text-xs lg:text-sm">
            <Coins className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Currencies</span>
          </TabsTrigger>
          <TabsTrigger value="backup" className="text-xs lg:text-sm">
            <Database className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Backup</span>
          </TabsTrigger>
          <TabsTrigger value="role-permissions" className="text-xs lg:text-sm" data-testid="tab-role-permissions">
            <Shield className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Role Permissions</span>
          </TabsTrigger>
          <TabsTrigger value="default-accounts" className="text-xs lg:text-sm" data-testid="tab-default-accounts">
            <Landmark className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Default Accounts</span>
          </TabsTrigger>
          <TabsTrigger value="barcode" className="text-xs lg:text-sm" data-testid="tab-barcode">
            <Hash className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Barcode</span>
          </TabsTrigger>
          <TabsTrigger value="label-options" className="text-xs lg:text-sm" data-testid="tab-label-options">
            <Tag className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Label Options</span>
          </TabsTrigger>
          {user?.role === 'super_admin' && (
            <TabsTrigger value="fix-trial-balance" className="text-xs lg:text-sm" data-testid="tab-fix-trial-balance">
              <Scale className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Fix Trial Balance</span>
            </TabsTrigger>
          )}
          {user?.role === 'super_admin' && (
            <TabsTrigger value="branding" className="text-xs lg:text-sm" data-testid="tab-branding">
              <Sparkles className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
          )}
          {user?.role === 'super_admin' && (
            <TabsTrigger value="tools" className="text-xs lg:text-sm" data-testid="tab-tools">
              <Terminal className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Tools</span>
            </TabsTrigger>
          )}
          {user?.role === 'super_admin' && (
            <TabsTrigger value="ai-integration" className="text-xs lg:text-sm" data-testid="tab-ai-integration">
              <Bot className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Integrations</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  User Management
                </CardTitle>
                <div className="flex gap-2">
                  <Select value={selectedOrgFilter} onValueChange={setSelectedOrgFilter}>
                    <SelectTrigger className="w-[180px] text-xs lg:text-sm" data-testid="org-filter">
                      <SelectValue placeholder="Filter by org" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Organizations</SelectItem>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Dialog open={isUserDialogOpen} onOpenChange={(open) => { setIsUserDialogOpen(open); if (!open) resetUserForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="btn-glow text-xs lg:text-sm" data-testid="add-user-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                        {!editingUser && (
                          <>
                            <div className="space-y-2">
                              <Label className="text-sm">Email</Label>
                              <Input
                                type="email"
                                placeholder="user@example.com"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                required
                                data-testid="user-email-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Password</Label>
                              <Input
                                type="password"
                                placeholder="Initial password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                required={!editingUser}
                                data-testid="user-password-input"
                              />
                            </div>
                          </>
                        )}
                        <div className="space-y-2">
                          <Label className="text-sm">Name</Label>
                          <Input
                            placeholder="Full name"
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            required
                            data-testid="user-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Role</Label>
                          <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                            <SelectTrigger data-testid="user-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="accountant">Accountant</SelectItem>
                              <SelectItem value="cashier">Cashier</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Organization</Label>
                          <Select value={newUser.organization_id || 'none'} onValueChange={(value) => setNewUser({ ...newUser, organization_id: value === 'none' ? '' : value })}>
                            <SelectTrigger data-testid="user-org-select">
                              <SelectValue placeholder="Select organization" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Organization</SelectItem>
                              {organizations.map(org => (
                                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => { setIsUserDialogOpen(false); resetUserForm(); }}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="save-user-btn">
                            {editingUser ? 'Update' : 'Create'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner" /></div>
              ) : users.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No users found</p>
              ) : (
                <>
                  {/* Mobile view */}
                  <div className="lg:hidden space-y-3">
                    {users.map((u) => (
                      <div key={u.id} className="p-3 bg-muted/20 rounded-sm border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="font-medium text-sm">{u.name}</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-sm text-xs border ${roleColors[u.role]}`}>
                            {getRoleDisplayName(u.role)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">{u.organization_name || 'No organization'}</p>
                        <div className="flex gap-1 mt-2">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleEditUser(u)}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setResetPasswordUser(u)}>
                            <Key className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleToggleActive(u)}>
                            {u.is_active ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                          </Button>
                          {u.id !== user.id && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => setDeleteConfirm(u)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop view */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Organization</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} data-testid={`user-row-${u.id}`}>
                            <td>
                              <span className={`w-2 h-2 rounded-full inline-block ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            </td>
                            <td className="font-medium">{u.name}</td>
                            <td className="text-muted-foreground">{u.email}</td>
                            <td>
                              <span className={`px-2 py-0.5 rounded-sm text-xs border ${roleColors[u.role]}`}>
                                {getRoleDisplayName(u.role)}
                              </span>
                            </td>
                            <td className="text-muted-foreground">{u.organization_name || '-'}</td>
                            <td className="text-muted-foreground text-xs">{formatDate(u.created_at)}</td>
                            <td>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditUser(u)} title="Edit">
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setResetPasswordUser(u)} title="Reset Password">
                                  <Key className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'}>
                                  {u.is_active ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                                </Button>
                                {u.id !== user.id && (
                                  <Button variant="ghost" size="sm" className="text-red-400" onClick={() => setDeleteConfirm(u)} title="Delete">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Organizations
                </CardTitle>
                <Dialog open={isOrgDialogOpen} onOpenChange={(open) => { setIsOrgDialogOpen(open); if (!open) resetOrgForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="btn-glow text-xs lg:text-sm" data-testid="add-org-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Organization
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingOrg ? 'Edit Organization' : 'Add New Organization'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateOrg} className="space-y-3 mt-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Organization Name</Label>
                        <Input
                          placeholder="e.g., Beirut Trading Co."
                          value={newOrg.name}
                          onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                          required
                          data-testid="org-name-input"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Organization Name (Arabic)</Label>
                        <Input
                          placeholder="مثال: شركة بيروت التجارية"
                          value={newOrg.name_ar}
                          onChange={(e) => setNewOrg({ ...newOrg, name_ar: e.target.value })}
                          data-testid="org-name-ar-input"
                          className="h-8 text-sm text-right"
                          dir="rtl"
                        />
                      </div>

                      {/* Coffee shop tagline */}
                      <div className="space-y-1">
                        <Label className="text-xs">Tagline / Subtitle <span className="text-muted-foreground">(shown under shop name on POS)</span></Label>
                        <Input
                          placeholder="e.g. Coffee shop, Bakery & Café, Specialty Roastery"
                          value={newOrg.tagline || ''}
                          onChange={(e) => setNewOrg({ ...newOrg, tagline: e.target.value })}
                          data-testid="org-tagline-input"
                          className="h-8 text-sm"
                        />
                      </div>

                      {/* Logo upload */}
                      <div className="space-y-1">
                        <Label className="text-xs">Organization Logo <span className="text-muted-foreground">(uploaded to S3, shown on POS header & receipts)</span></Label>
                        <div className="flex items-center gap-3 p-2 rounded-md border border-border bg-muted/30">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-amber-700 to-amber-900 flex items-center justify-center text-3xl flex-shrink-0">
                            {newOrg.logo_url ? (
                              <img src={newOrg.logo_url} alt="Logo" className="w-full h-full object-contain bg-white"
                                onError={(e) => { e.target.style.display='none'; }}
                              />
                            ) : (
                              <span>☕</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {newOrg.logo_url ? (
                              <div className="text-xs text-muted-foreground truncate" title={newOrg.logo_url}>{newOrg.logo_url}</div>
                            ) : (
                              <div className="text-xs text-muted-foreground">No logo uploaded</div>
                            )}
                            <div className="flex gap-2 mt-2">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                  className="hidden"
                                  onChange={handleLogoUpload}
                                  disabled={logoUploading || !editingOrg}
                                  data-testid="org-logo-upload-input"
                                />
                                <span className={`px-3 py-1.5 text-xs rounded-md ${logoUploading ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                                  {logoUploading ? 'Uploading…' : (newOrg.logo_url ? 'Replace logo' : 'Upload logo')}
                                </span>
                              </label>
                              {newOrg.logo_url && (
                                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={handleLogoRemove}>
                                  Remove
                                </Button>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">PNG / JPG / WebP / SVG, max 4 MB. Requires AWS S3 credentials configured in Integrations tab.</div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Base Currency</Label>
                          <Select value={newOrg.currency} onValueChange={(value) => setNewOrg({ ...newOrg, currency: value })}>
                            <SelectTrigger data-testid="org-currency-select" className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LBP">LBP - Lebanese Pound</SelectItem>
                              <SelectItem value="USD">USD - US Dollar</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Exchange Rate (LBP/USD)</Label>
                          <Input
                            type="number"
                            value={newOrg.base_exchange_rate}
                            onChange={(e) => setNewOrg({ ...newOrg, base_exchange_rate: parseFloat(e.target.value) })}
                            required
                            className="font-mono h-8 text-sm"
                            data-testid="org-rate-input"
                          />
                        </div>
                      </div>
                      
                      {/* VAT Registration Toggle */}
                      <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                        <div>
                          <Label className="text-xs font-medium">VAT Registered</Label>
                          <p className="text-xs text-muted-foreground">
                            Enable tax calculations throughout the app
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newOrg.vat_registered}
                            onChange={(e) => setNewOrg({ ...newOrg, vat_registered: e.target.checked })}
                            className="sr-only peer"
                            data-testid="org-vat-registered-toggle"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      </div>
                      
                      {/* Tax Settings - only show if VAT registered */}
                      {newOrg.vat_registered && (
                      <>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Default Tax Rate (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={newOrg.tax_percent}
                            onChange={(e) => {
                              const val = e.target.value;
                              setNewOrg({ ...newOrg, tax_percent: val === '' ? 0 : parseFloat(val) });
                            }}
                            className="font-mono h-8 text-sm"
                            data-testid="org-tax-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Tax Name</Label>
                          <Select value={newOrg.tax_name} onValueChange={(v) => setNewOrg({ ...newOrg, tax_name: v })}>
                            <SelectTrigger data-testid="org-tax-name-select" className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="VAT">VAT</SelectItem>
                              <SelectItem value="Sales Tax">Sales Tax</SelectItem>
                              <SelectItem value="GST">GST</SelectItem>
                              <SelectItem value="Tax">Tax</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* TTC / HT Toggles */}
                      <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                        <div className="pr-2">
                          <Label className="text-xs font-medium">Sale prices include VAT (TTC)</Label>
                          <p className="text-[11px] text-muted-foreground leading-snug">
                            ON: Menu/POS prices already include VAT (extracted on receipt). OFF: VAT is added on top at checkout.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={newOrg.prices_include_tax !== false}
                            onChange={(e) => setNewOrg({ ...newOrg, prices_include_tax: e.target.checked })}
                            className="sr-only peer"
                            data-testid="org-prices-include-tax-toggle"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                        <div className="pr-2">
                          <Label className="text-xs font-medium">Cost prices include VAT (TTC)</Label>
                          <p className="text-[11px] text-muted-foreground leading-snug">
                            Typically OFF for raw materials (suppliers invoice HT). Affects cost reporting only.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={newOrg.costs_include_tax === true}
                            onChange={(e) => setNewOrg({ ...newOrg, costs_include_tax: e.target.checked })}
                            className="sr-only peer"
                            data-testid="org-costs-include-tax-toggle"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                      </>
                      )}
                      
                      {/* Contact Information */}
                      <div className="border-t pt-3 mt-3">
                        <h4 className="text-sm font-medium mb-2">Contact Information</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Phone</Label>
                            <Input
                              placeholder="e.g., +961 1 234 567"
                              value={newOrg.phone}
                              onChange={(e) => setNewOrg({ ...newOrg, phone: e.target.value })}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Email</Label>
                            <Input
                              type="email"
                              placeholder="e.g., info@company.com"
                              value={newOrg.email}
                              onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-1 mt-2">
                          <Label className="text-xs">Address</Label>
                          <Textarea
                            placeholder="e.g., Beirut, Lebanon"
                            value={newOrg.address}
                            onChange={(e) => setNewOrg({ ...newOrg, address: e.target.value })}
                            rows={1}
                            className="text-sm min-h-[32px]"
                          />
                        </div>
                        <div className="space-y-1 mt-2">
                          <Label className="text-xs">Address (Arabic)</Label>
                          <Textarea
                            placeholder="مثال: بيروت، لبنان"
                            value={newOrg.address_ar}
                            onChange={(e) => setNewOrg({ ...newOrg, address_ar: e.target.value })}
                            rows={1}
                            className="text-sm min-h-[32px] text-right"
                            dir="rtl"
                          />
                        </div>
                        <div className="space-y-1 mt-2">
                          <Label className="text-xs">Registration Number</Label>
                          <Input
                            placeholder="e.g., RC 12345"
                            value={newOrg.registration_number}
                            onChange={(e) => setNewOrg({ ...newOrg, registration_number: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        
                        {/* Inventory Settings */}
                        <div className="border-t pt-3 mt-3">
                          <h4 className="text-sm font-medium mb-2">Inventory Settings</h4>
                          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                            <div>
                              <Label className="text-xs font-medium">Enable Expiry Tracking</Label>
                              <p className="text-xs text-muted-foreground">
                                Track inventory by batches with expiry dates
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newOrg.enable_expiry_tracking}
                                onChange={(e) => setNewOrg({ ...newOrg, enable_expiry_tracking: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                            </label>
                          </div>
                        </div>
                        
                        {/* POS Settings */}
                        <div className="border-t pt-3 mt-3">
                          <h4 className="text-sm font-medium mb-2">POS Settings</h4>
                          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                            <div>
                              <Label className="text-xs font-medium">Show Quick Items Panel</Label>
                              <p className="text-xs text-muted-foreground">
                                Display quick item buttons in POS
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newOrg.pos_quick_items_enabled}
                                onChange={(e) => setNewOrg({ ...newOrg, pos_quick_items_enabled: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                            </label>
                          </div>

                          {/* POS Layout Visibility group */}
                          <div className="mt-2 space-y-2">
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-2 mb-1">POS Layout Visibility</p>

                            {/* Show Scan Barcode */}
                            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30 border">
                              <div>
                                <Label className="text-xs font-medium">Show Scan Barcode</Label>
                                <p className="text-[10px] text-muted-foreground">Display the &ldquo;Scan barcode&hellip;&rdquo; input and camera button at the top of POS. Hardware scanners still work even when hidden.</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={newOrg.pos_show_barcode !== false}
                                  onChange={(e) => setNewOrg({ ...newOrg, pos_show_barcode: e.target.checked })}
                                  className="sr-only peer"
                                  data-testid="org-show-barcode-toggle"
                                />
                                <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                              </label>
                            </div>

                            {/* Show Search Items */}
                            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30 border">
                              <div>
                                <Label className="text-xs font-medium">Show Search Items</Label>
                                <p className="text-[10px] text-muted-foreground">Display the &ldquo;Search Items&hellip;&rdquo; box and the inline Add-Item (+) button.</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={newOrg.pos_show_search !== false}
                                  onChange={(e) => setNewOrg({ ...newOrg, pos_show_search: e.target.checked })}
                                  className="sr-only peer"
                                  data-testid="org-show-search-toggle"
                                />
                                <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                              </label>
                            </div>

                            {/* Show Customer Selector */}
                            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30 border">
                              <div>
                                <Label className="text-xs font-medium">Show Customer Selector</Label>
                                <p className="text-[10px] text-muted-foreground">Display the &ldquo;Select customer&hellip;&rdquo; card in the cart panel. When hidden, sales default to walk-in customer.</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={newOrg.pos_show_customer !== false}
                                  onChange={(e) => setNewOrg({ ...newOrg, pos_show_customer: e.target.checked })}
                                  className="sr-only peer"
                                  data-testid="org-show-customer-toggle"
                                />
                                <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                              </label>
                            </div>
                          </div>

                          {/* On-screen keyboard toggle */}
                          <div className="flex items-center justify-between p-2 bg-muted/30 rounded-md mt-2">
                            <div>
                              <Label className="text-xs cursor-pointer">On-Screen Keyboard</Label>
                              <p className="text-[10px] text-muted-foreground">Show a coffee-themed virtual keyboard (EN / AR / RU) — ideal for tablet POS.</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newOrg.pos_keyboard_enabled || false}
                                onChange={(e) => setNewOrg({ ...newOrg, pos_keyboard_enabled: e.target.checked })}
                                className="sr-only peer"
                                data-testid="org-keyboard-enabled-toggle"
                              />
                              <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-500"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-3">
                        <Button type="button" variant="outline" onClick={() => { setIsOrgDialogOpen(false); resetOrgForm(); }}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="save-org-btn">{editingOrg ? 'Update' : 'Create'}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {organizations.map((org) => (
                  <div key={org.id} className="p-4 bg-muted/20 rounded-sm border border-border" data-testid={`org-card-${org.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{org.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Currency: {org.currency}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Rate: {org.base_exchange_rate.toLocaleString()} LBP/USD
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Tax: {org.tax_percent !== undefined ? org.tax_percent : 11}% ({org.tax_name || 'VAT'})
                        </p>
                      </div>
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        {users.filter(u => u.organization_id === org.id).length} users assigned
                      </p>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleEditOrg(org)} title="Edit">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => setDeleteOrgConfirm(org)} title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fiscal Years Tab */}
        <TabsContent value="fiscal-years" className="space-y-4" data-testid="fiscal-years-tab">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Fiscal Year Management
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Define and manage fiscal years for {currentOrg?.name || 'your organization'}
                  </p>
                </div>
                <Button onClick={openCreateFY} size="sm" data-testid="create-fy-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  New Fiscal Year
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {fiscalYears.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">No fiscal years defined yet</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Create your first fiscal year to enable period-based reporting and posting controls.
                  </p>
                  <Button onClick={openCreateFY} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Create First Fiscal Year
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {fiscalYears.map((fy) => (
                    <div 
                      key={fy.id} 
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border ${
                        fy.status === 'closed' ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/30 bg-emerald-500/5'
                      }`}
                      data-testid={`fy-item-${fy.id}`}
                    >
                      <div className="flex items-center gap-3 mb-2 sm:mb-0">
                        <div className={`p-2 rounded-lg ${
                          fy.status === 'closed' ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                        }`}>
                          {fy.status === 'closed' ? (
                            <Lock className="w-5 h-5 text-amber-400" />
                          ) : (
                            <Unlock className="w-5 h-5 text-emerald-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-sm">{fy.name}</h3>
                          <p className="text-xs text-muted-foreground">
                            {fy.start_date} to {fy.end_date}
                          </p>
                          {fy.closed_at && (
                            <p className="text-xs text-amber-400 mt-0.5">
                              Closed on {new Date(fy.closed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          fy.status === 'open' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {fy.status === 'open' ? 'Open' : 'Closed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {fy.status === 'open' && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openEditFY(fy)}
                              data-testid={`edit-fy-${fy.id}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                              onClick={() => { setCloseConfirmFY(fy); setCloseResult(null); }}
                              data-testid={`close-fy-${fy.id}`}
                            >
                              <Lock className="w-3 h-3 mr-1" />
                              Close FY
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-destructive border-destructive/50 hover:bg-destructive/10"
                              onClick={() => setDeleteFYConfirm(fy)}
                              data-testid={`delete-fy-${fy.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {fy.status === 'closed' && user?.role === 'super_admin' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                            onClick={() => handleReopenFY(fy)}
                            data-testid={`reopen-fy-${fy.id}`}
                          >
                            <Unlock className="w-3 h-3 mr-1" />
                            Reopen
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create/Edit FY Dialog */}
          <Dialog open={isFYDialogOpen} onOpenChange={(open) => { setIsFYDialogOpen(open); if (!open) setEditingFY(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingFY ? 'Edit Fiscal Year' : 'Create Fiscal Year'}</DialogTitle>
                <DialogDescription>
                  {editingFY ? 'Update the fiscal year details.' : 'Define a new fiscal year for this organization.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={editingFY ? handleUpdateFY : handleCreateFY} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newFY.name}
                    onChange={(e) => setNewFY({...newFY, name: e.target.value})}
                    placeholder="e.g., FY 2024, FY 2024-2025"
                    required
                    data-testid="fy-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={newFY.start_date}
                      onChange={(e) => setNewFY({...newFY, start_date: e.target.value})}
                      required
                      data-testid="fy-start-date"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={newFY.end_date}
                      onChange={(e) => setNewFY({...newFY, end_date: e.target.value})}
                      required
                      data-testid="fy-end-date"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsFYDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={fyLoading} data-testid="fy-submit-btn">
                    {fyLoading ? 'Saving...' : (editingFY ? 'Update' : 'Create')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Close FY Confirmation Dialog */}
          <Dialog open={!!closeConfirmFY} onOpenChange={(open) => { if (!open) { setCloseConfirmFY(null); setCloseResult(null); } }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  Close Fiscal Year
                </DialogTitle>
                <DialogDescription>
                  {closeResult ? 'Fiscal year has been closed successfully.' : `Are you sure you want to close "${closeConfirmFY?.name}"?`}
                </DialogDescription>
              </DialogHeader>
              
              {!closeResult ? (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                    <p className="font-medium text-amber-400 mb-1">This action will:</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground text-xs">
                      <li>Lock the fiscal year - no new vouchers/invoices can be posted</li>
                      <li>Generate closing entries (Revenue/Expenses to Retained Earnings)</li>
                      <li>The closing can be reversed by a Super Admin via "Reopen"</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-card border rounded-lg text-xs text-muted-foreground">
                    <p><strong>Period:</strong> {closeConfirmFY?.start_date} to {closeConfirmFY?.end_date}</p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCloseConfirmFY(null)}>Cancel</Button>
                    <Button 
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      onClick={handleCloseFY}
                      disabled={closingFY}
                      data-testid="confirm-close-fy"
                    >
                      {closingFY ? 'Closing...' : 'Close Fiscal Year'}
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm">
                    <p className="font-medium text-emerald-400 mb-2">Closing Summary</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <p>Revenue (LBP):</p>
                      <p className="text-right font-mono">{closeResult.revenue_total_lbp?.toLocaleString()}</p>
                      <p>Revenue (USD):</p>
                      <p className="text-right font-mono">${closeResult.revenue_total_usd?.toLocaleString()}</p>
                      <p>Expenses (LBP):</p>
                      <p className="text-right font-mono">{closeResult.expense_total_lbp?.toLocaleString()}</p>
                      <p>Expenses (USD):</p>
                      <p className="text-right font-mono">${closeResult.expense_total_usd?.toLocaleString()}</p>
                      <div className="col-span-2 border-t pt-1 mt-1"></div>
                      <p className="font-medium">Net Income (LBP):</p>
                      <p className={`text-right font-mono font-medium ${closeResult.net_income_lbp >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {closeResult.net_income_lbp?.toLocaleString()}
                      </p>
                      <p className="font-medium">Net Income (USD):</p>
                      <p className={`text-right font-mono font-medium ${closeResult.net_income_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${closeResult.net_income_usd?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => { setCloseConfirmFY(null); setCloseResult(null); }}>Done</Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete FY Confirmation Dialog */}
          <Dialog open={!!deleteFYConfirm} onOpenChange={(open) => { if (!open) setDeleteFYConfirm(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Fiscal Year</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{deleteFYConfirm?.name}"? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteFYConfirm(null)}>Cancel</Button>
                <Button variant="destructive" onClick={handleDeleteFY} data-testid="confirm-delete-fy">Delete</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Service Items Tab */}
        <TabsContent value="services" className="space-y-4">
          <ServiceManagement />
        </TabsContent>

        {/* Invoice Template Tab */}
        <TabsContent value="invoice-template" className="space-y-4">
          {!currentOrg ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Please select an organization to configure its invoice template.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <DocumentTemplateManager 
              currentOrg={currentOrg}
              fetchOrganizations={fetchOrganizations}
            />
          )}
        </TabsContent>

        {/* Document Series Tab */}
        <TabsContent value="document-series" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Document Number Series
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Configure the prefix and starting number for each document type. 
                Useful when migrating from another system or continuing an existing series.
              </p>
            </CardHeader>
            <CardContent>
              {!currentOrg ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Please select an organization to configure document series.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Sales Invoice Series */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Sales Invoice Series
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={invoiceSeries.sales_invoice.prefix}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            sales_invoice: { ...invoiceSeries.sales_invoice, prefix: e.target.value }
                          })}
                          placeholder="INV-"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">e.g., INV-, SI-, SALE-</p>
                      </div>
                      <div>
                        <Label className="text-xs">Next Number (Override)</Label>
                        <Input
                          type="number"
                          value={invoiceSeries.sales_invoice.next_number}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            sales_invoice: { ...invoiceSeries.sales_invoice, next_number: e.target.value }
                          })}
                          placeholder="Auto"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty for auto-increment</p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={invoiceSeries.sales_invoice.include_year}
                            onChange={(e) => setInvoiceSeries({
                              ...invoiceSeries,
                              sales_invoice: { ...invoiceSeries.sales_invoice, include_year: e.target.checked }
                            })}
                            className="rounded"
                          />
                          Include Year (e.g., INV-2026-)
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-500 mt-2">
                      Preview: {invoiceSeries.sales_invoice.prefix}{invoiceSeries.sales_invoice.include_year ? `${new Date().getFullYear()}-` : ''}{String(invoiceSeries.sales_invoice.next_number || 1).padStart(5, '0')}
                    </p>
                  </div>

                  {/* Purchase Invoice Series */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Purchase Invoice Series
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={invoiceSeries.purchase_invoice.prefix}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            purchase_invoice: { ...invoiceSeries.purchase_invoice, prefix: e.target.value }
                          })}
                          placeholder="PUR-"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">e.g., PUR-, PI-, PINV-</p>
                      </div>
                      <div>
                        <Label className="text-xs">Next Number (Override)</Label>
                        <Input
                          type="number"
                          value={invoiceSeries.purchase_invoice.next_number}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            purchase_invoice: { ...invoiceSeries.purchase_invoice, next_number: e.target.value }
                          })}
                          placeholder="Auto"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty for auto-increment</p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={invoiceSeries.purchase_invoice.include_year}
                            onChange={(e) => setInvoiceSeries({
                              ...invoiceSeries,
                              purchase_invoice: { ...invoiceSeries.purchase_invoice, include_year: e.target.checked }
                            })}
                            className="rounded"
                          />
                          Include Year
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-500 mt-2">
                      Preview: {invoiceSeries.purchase_invoice.prefix}{invoiceSeries.purchase_invoice.include_year ? `${new Date().getFullYear()}-` : ''}{String(invoiceSeries.purchase_invoice.next_number || 1).padStart(5, '0')}
                    </p>
                  </div>

                  {/* POS Receipt Series */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Coins className="w-4 h-4" />
                      POS Receipt Series
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={invoiceSeries.pos.prefix}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            pos: { ...invoiceSeries.pos, prefix: e.target.value }
                          })}
                          placeholder="POS-"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">e.g., POS-, REC-, CASH-</p>
                      </div>
                      <div>
                        <Label className="text-xs">Next Number (Override)</Label>
                        <Input
                          type="number"
                          value={invoiceSeries.pos.next_number}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            pos: { ...invoiceSeries.pos, next_number: e.target.value }
                          })}
                          placeholder="Auto"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty for auto-increment</p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={invoiceSeries.pos.include_year}
                            onChange={(e) => setInvoiceSeries({
                              ...invoiceSeries,
                              pos: { ...invoiceSeries.pos, include_year: e.target.checked }
                            })}
                            className="rounded"
                          />
                          Include Year
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-500 mt-2">
                      Preview: {invoiceSeries.pos.prefix}{invoiceSeries.pos.include_year ? `${new Date().getFullYear()}-` : ''}{String(invoiceSeries.pos.next_number || 1).padStart(5, '0')}
                    </p>
                  </div>

                  {/* Debit/Credit Note Series */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Debit/Credit Note Series
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={invoiceSeries.dbcr.prefix}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            dbcr: { ...invoiceSeries.dbcr, prefix: e.target.value }
                          })}
                          placeholder="DBCR-"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">e.g., DBCR-, DC-, NOTE-</p>
                      </div>
                      <div>
                        <Label className="text-xs">Next Number (Override)</Label>
                        <Input
                          type="number"
                          value={invoiceSeries.dbcr.next_number}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            dbcr: { ...invoiceSeries.dbcr, next_number: e.target.value }
                          })}
                          placeholder="Auto"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty for auto-increment</p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={invoiceSeries.dbcr.include_year}
                            onChange={(e) => setInvoiceSeries({
                              ...invoiceSeries,
                              dbcr: { ...invoiceSeries.dbcr, include_year: e.target.checked }
                            })}
                            className="rounded"
                          />
                          Include Year
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-500 mt-2">
                      Preview: {invoiceSeries.dbcr.prefix}{invoiceSeries.dbcr.include_year ? `${new Date().getFullYear()}-` : ''}{String(invoiceSeries.dbcr.next_number || 1).padStart(5, '0')}
                    </p>
                  </div>

                  {/* Sales Quotation Series */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Sales Quotation Series
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={invoiceSeries.quotation.prefix}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            quotation: { ...invoiceSeries.quotation, prefix: e.target.value }
                          })}
                          placeholder="QUO-"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">e.g., QUO-, QUOTE-, EST-</p>
                      </div>
                      <div>
                        <Label className="text-xs">Next Number (Override)</Label>
                        <Input
                          type="number"
                          value={invoiceSeries.quotation.next_number}
                          onChange={(e) => setInvoiceSeries({
                            ...invoiceSeries,
                            quotation: { ...invoiceSeries.quotation, next_number: e.target.value }
                          })}
                          placeholder="Auto"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty for auto-increment</p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={invoiceSeries.quotation.include_year}
                            onChange={(e) => setInvoiceSeries({
                              ...invoiceSeries,
                              quotation: { ...invoiceSeries.quotation, include_year: e.target.checked }
                            })}
                            className="rounded"
                          />
                          Include Year
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-cyan-500 mt-2">
                      Preview: {invoiceSeries.quotation.prefix}{invoiceSeries.quotation.include_year ? `${new Date().getFullYear()}-` : ''}{String(invoiceSeries.quotation.next_number || 1).padStart(5, '0')}
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSaveInvoiceSeries} disabled={seriesSaving}>
                      {seriesSaving ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Save Series Settings
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currencies Tab */}
        <TabsContent value="currencies" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Currency Management
                </CardTitle>
                <div className="flex gap-2">
                  {currencies.length === 0 && (
                    <Button variant="outline" onClick={handleSeedCurrencies} disabled={seedingCurrencies} className="text-xs lg:text-sm">
                      <RefreshCw className={`w-4 h-4 mr-2 ${seedingCurrencies ? 'animate-spin' : ''}`} />
                      Seed Currencies
                    </Button>
                  )}
                  <Dialog open={isCurrencyDialogOpen} onOpenChange={(open) => { setIsCurrencyDialogOpen(open); if (!open) resetCurrencyForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="btn-glow text-xs lg:text-sm" data-testid="add-currency-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Currency
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingCurrency ? 'Edit Currency' : 'Add New Currency'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateCurrency} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm">Currency Code (ISO)</Label>
                            <Input
                              placeholder="e.g., EUR"
                              value={newCurrency.code}
                              onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                              required
                              disabled={!!editingCurrency}
                              maxLength={3}
                              className="font-mono"
                              data-testid="currency-code-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Symbol</Label>
                            <Input
                              placeholder="e.g., €"
                              value={newCurrency.symbol}
                              onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                              required
                              data-testid="currency-symbol-input"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Currency Name</Label>
                          <Input
                            placeholder="e.g., Euro"
                            value={newCurrency.name}
                            onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                            required
                            data-testid="currency-name-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm">Rate to USD</Label>
                            <Input
                              type="number"
                              step="0.0001"
                              value={newCurrency.rate_to_usd}
                              onChange={(e) => setNewCurrency({ ...newCurrency, rate_to_usd: parseFloat(e.target.value) || 0 })}
                              required
                              className="font-mono"
                              data-testid="currency-rate-usd-input"
                            />
                            <p className="text-xs text-muted-foreground">1 {newCurrency.code || 'XXX'} = {newCurrency.rate_to_usd} USD</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Rate to LBP</Label>
                            <Input
                              type="number"
                              step="1"
                              value={newCurrency.rate_to_lbp}
                              onChange={(e) => setNewCurrency({ ...newCurrency, rate_to_lbp: parseFloat(e.target.value) || 0 })}
                              required
                              className="font-mono"
                              data-testid="currency-rate-lbp-input"
                            />
                            <p className="text-xs text-muted-foreground">1 {newCurrency.code || 'XXX'} = {newCurrency.rate_to_lbp.toLocaleString()} LBP</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="currency-active"
                            checked={newCurrency.is_active}
                            onChange={(e) => setNewCurrency({ ...newCurrency, is_active: e.target.checked })}
                            className="rounded"
                          />
                          <Label htmlFor="currency-active" className="text-sm cursor-pointer">Active (available for voucher entry)</Label>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => { setIsCurrencyDialogOpen(false); resetCurrencyForm(); }}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="save-currency-btn">{editingCurrency ? 'Update' : 'Create'}</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {currencies.length === 0 ? (
                <div className="text-center py-8">
                  <Coins className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No currencies configured</p>
                  <Button variant="outline" onClick={handleSeedCurrencies} disabled={seedingCurrencies}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${seedingCurrencies ? 'animate-spin' : ''}`} />
                    Seed World Currencies
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Code</th>
                        <th>Symbol</th>
                        <th>Name</th>
                        <th className="text-right">Rate to USD</th>
                        <th className="text-right">Rate to LBP</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currencies.map((c) => (
                        <tr key={c.id} data-testid={`currency-row-${c.code}`}>
                          <td>
                            <span className={`w-2 h-2 rounded-full inline-block ${c.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          </td>
                          <td className="font-mono font-medium">{c.code}</td>
                          <td>{c.symbol}</td>
                          <td className="text-muted-foreground">{c.name}</td>
                          <td className="text-right font-mono">{c.rate_to_usd.toFixed(4)}</td>
                          <td className="text-right font-mono">{c.rate_to_lbp.toLocaleString()}</td>
                          <td>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditCurrency(c)} title="Edit">
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleToggleCurrencyActive(c)} title={c.is_active ? 'Deactivate' : 'Activate'}>
                                {c.is_active ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                              </Button>
                              {!['USD', 'LBP'].includes(c.code) && (
                                <Button variant="ghost" size="sm" className="text-red-400" onClick={() => setDeleteCurrencyConfirm(c)} title="Delete">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup & Restore Tab */}
        <TabsContent value="backup" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Database className="w-5 h-5" />
                Backup & Restore
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Backup Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">Create Backup</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Download a complete backup of your database including all organizations, users, accounts, vouchers, and currencies.
                </p>
                
                {backupInfo && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Object.entries(backupInfo.collections || {}).map(([name, count]) => (
                      <div key={name} className="p-3 bg-muted/30 rounded-sm text-center">
                        <p className="text-lg font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground capitalize">{name}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={() => handleCreateBackup()} 
                    disabled={backupLoading}
                    className="btn-glow"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {backupLoading ? 'Creating Backup...' : 'Download Full Backup'}
                  </Button>
                  {currentOrg && (
                    <Button 
                      variant="outline"
                      onClick={() => handleCreateBackup(currentOrg.id)} 
                      disabled={backupLoading}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Backup {currentOrg.name} Only
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Restore Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4 text-orange-400" />
                  <h3 className="font-semibold">Restore from Backup</h3>
                </div>
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-400">Warning</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Restoring a backup will modify your existing data. Make sure to download a backup of your current data first.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <div className="flex-1">
                    <input
                      ref={restoreFileRef}
                      type="file"
                      accept=".json"
                      onChange={handleRestoreFileSelect}
                      className="hidden"
                      id="restore-file"
                    />
                    <label htmlFor="restore-file">
                      <Button variant="outline" asChild className="cursor-pointer">
                        <span>
                          <FileJson className="w-4 h-4 mr-2" />
                          Select Backup File (.json)
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                {restoreResult && (
                  <div className={`p-4 rounded-sm ${restoreResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {restoreResult.success ? (
                        <Check className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                      <span className="font-medium">{restoreResult.message}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {Object.entries(restoreResult.restored_counts || {}).map(([name, counts]) => (
                        <div key={name} className="text-xs">
                          <span className="text-muted-foreground capitalize">{name}:</span>
                          {typeof counts === 'object' ? (
                            <span className="ml-1">
                              {counts.inserted > 0 && <span className="text-emerald-400">+{counts.inserted}</span>}
                              {counts.updated > 0 && <span className="text-blue-400 ml-1">↻{counts.updated}</span>}
                              {counts.error && <span className="text-red-400">{counts.error}</span>}
                            </span>
                          ) : (
                            <span className="ml-1">{counts}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Role Permissions Tab */}
        <TabsContent value="role-permissions" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Role Permissions</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Configure which modules each role can access. Super Admin always has full access.</p>
              </div>
              <Button onClick={saveRolePermissions} disabled={rolePermSaving} data-testid="save-role-permissions-btn">
                {rolePermSaving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-3 min-w-[200px] sticky left-0 bg-background">Module</th>
                      <th className="text-center p-3 w-[100px]">
                        <span className="text-violet-400 font-semibold">Super Admin</span>
                      </th>
                      {CONFIGURABLE_ROLES.map(role => (
                        <th key={role} className="text-center p-3 w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${roleColors[role] || 'bg-zinc-500/20'}`}>
                              {getRoleDisplayName(role)}
                            </span>
                            <div className="flex gap-1 mt-1">
                              <button className="text-[10px] text-green-500 hover:underline" onClick={() => toggleAllForRole(role, true)}>All On</button>
                              <span className="text-muted-foreground">|</span>
                              <button className="text-[10px] text-red-400 hover:underline" onClick={() => toggleAllForRole(role, false)}>All Off</button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_MODULES.map((mod) => {
                      const isExpanded = expandedModules[mod.path];
                      return (
                        <React.Fragment key={mod.path}>
                          <tr className="border-b hover:bg-muted/10">
                            <td className="p-3 font-medium sticky left-0 bg-background">
                              <button 
                                onClick={() => toggleExpandModule(mod.path)}
                                className="flex items-center gap-2 hover:text-primary"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                {mod.label}
                              </button>
                            </td>
                            <td className="text-center p-3">
                              <div className="flex justify-center">
                                <div className="w-10 h-5 rounded-full bg-violet-500/30 flex items-center justify-end px-0.5 cursor-not-allowed" title="Super Admin always has full access">
                                  <div className="w-4 h-4 rounded-full bg-violet-400"></div>
                                </div>
                              </div>
                            </td>
                            {CONFIGURABLE_ROLES.map(role => {
                              const hasAccess = hasModuleAccess(role, mod.path);
                              const actions = getModuleActions(role, mod.path);
                              const actionCount = actions.length;
                              return (
                                <td key={role} className="text-center p-3">
                                  <div className="flex flex-col items-center gap-1">
                                    <button
                                      data-testid={`perm-${role}-${mod.path.replace(/\//g, '')}`}
                                      onClick={() => toggleRoleModule(role, mod.path)}
                                      className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${hasAccess ? 'bg-green-500/30 justify-end' : 'bg-red-500/20 justify-start'}`}
                                    >
                                      <div className={`w-4 h-4 rounded-full transition-colors ${hasAccess ? 'bg-green-400' : 'bg-red-400/60'}`}></div>
                                    </button>
                                    {hasAccess && (
                                      <span className="text-[10px] text-muted-foreground">{actionCount}/{ALL_ACTIONS.length}</span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          {/* Expanded Action Rows */}
                          {isExpanded && (
                            <tr className="bg-muted/5">
                              <td colSpan={CONFIGURABLE_ROLES.length + 2} className="p-0">
                                <div className="px-6 py-3 border-l-4 border-primary/30">
                                  <div className="grid grid-cols-[200px_repeat(5,1fr)] gap-2 items-center">
                                    <div className="text-xs font-semibold text-muted-foreground">Actions for {mod.label}:</div>
                                    {/* Super Admin column placeholder */}
                                    <div></div>
                                    {/* Configurable roles columns */}
                                    {CONFIGURABLE_ROLES.map(role => (
                                      <div key={`header-${role}`} className="text-center">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${roleColors[role] || 'bg-zinc-500/20'}`}>
                                          {getRoleDisplayName(role)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  {ALL_ACTIONS.map(action => (
                                    <div key={action} className="grid grid-cols-[200px_repeat(5,1fr)] gap-2 items-center mt-2">
                                      <div className="text-sm text-muted-foreground pl-4 flex items-center gap-2">
                                        {action === 'view' && <Eye className="w-3 h-3" />}
                                        {action === 'add' && <Plus className="w-3 h-3" />}
                                        {action === 'edit' && <Pencil className="w-3 h-3" />}
                                        {action === 'delete' && <Trash2 className="w-3 h-3" />}
                                        {action === 'print' && <Printer className="w-3 h-3" />}
                                        {action === 'ai' && <Sparkles className="w-3 h-3" />}
                                        {ACTION_LABELS[action]}
                                      </div>
                                      {/* Super Admin - always enabled */}
                                      <div className="flex justify-center">
                                        <div className="w-4 h-4 rounded bg-violet-400/50 flex items-center justify-center">
                                          <Check className="w-3 h-3 text-violet-200" />
                                        </div>
                                      </div>
                                      {/* Configurable roles */}
                                      {CONFIGURABLE_ROLES.map(role => {
                                        const hasAction = hasActionPermission(role, mod.path, action);
                                        return (
                                          <div key={`${role}-${action}`} className="flex justify-center">
                                            <button
                                              onClick={() => toggleRoleAction(role, mod.path, action)}
                                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                                hasAction 
                                                  ? 'bg-green-500/20 border-green-500 text-green-400' 
                                                  : 'bg-transparent border-muted-foreground/30 text-transparent hover:border-muted-foreground/50'
                                              }`}
                                              data-testid={`action-${role}-${mod.path.replace(/\//g, '')}-${action}`}
                                            >
                                              <Check className="w-3 h-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                  {/* Quick toggles for this module */}
                                  <div className="grid grid-cols-[200px_repeat(5,1fr)] gap-2 items-center mt-3 pt-2 border-t border-border/50">
                                    <div className="text-xs text-muted-foreground pl-4">Quick:</div>
                                    <div></div>
                                    {CONFIGURABLE_ROLES.map(role => (
                                      <div key={`quick-${role}`} className="flex justify-center gap-1">
                                        <button 
                                          className="text-[10px] text-green-500 hover:underline"
                                          onClick={() => toggleAllActionsForModule(role, mod.path, true)}
                                        >
                                          All
                                        </button>
                                        <span className="text-muted-foreground text-[10px]">|</span>
                                        <button 
                                          className="text-[10px] text-red-400 hover:underline"
                                          onClick={() => toggleAllActionsForModule(role, mod.path, false)}
                                        >
                                          None
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Default Accounts Tab */}
        <TabsContent value="default-accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="w-5 h-5" />
                Default Posting Accounts
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure default accounts used when posting transactions. Search by account code or name.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {defaultAccountsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sales VAT Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Sales VAT Account</Label>
                    <p className="text-xs text-muted-foreground">VAT collected on sales (e.g., 44270001)</p>
                    <div className="relative">
                      {defaultAccounts.sales_vat_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.sales_vat_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.sales_vat_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('sales_vat')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'sales_vat' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('sales_vat')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'sales_vat' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('sales_vat', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Purchase VAT Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Purchase VAT Account</Label>
                    <p className="text-xs text-muted-foreground">VAT paid on purchases (e.g., 44260001)</p>
                    <div className="relative">
                      {defaultAccounts.purchase_vat_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.purchase_vat_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.purchase_vat_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('purchase_vat')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'purchase_vat' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('purchase_vat')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'purchase_vat' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('purchase_vat', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sales Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Sales Account</Label>
                    <p className="text-xs text-muted-foreground">Revenue account for sales (e.g., 70110001)</p>
                    <div className="relative">
                      {defaultAccounts.sales_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.sales_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.sales_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('sales')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'sales' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('sales')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'sales' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('sales', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Purchase Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Purchase Account</Label>
                    <p className="text-xs text-muted-foreground">Expense account for purchases (e.g., 60110001)</p>
                    <div className="relative">
                      {defaultAccounts.purchase_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.purchase_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.purchase_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('purchase')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'purchase' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('purchase')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'purchase' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('purchase', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cash Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Cash Account</Label>
                    <p className="text-xs text-muted-foreground">Cash transactions (e.g., 53110001)</p>
                    <div className="relative">
                      {defaultAccounts.cash_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.cash_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.cash_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('cash')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'cash' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('cash')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'cash' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('cash', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bank Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Bank Account</Label>
                    <p className="text-xs text-muted-foreground">Bank transactions (e.g., 51110001)</p>
                    <div className="relative">
                      {defaultAccounts.bank_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.bank_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.bank_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('bank')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'bank' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('bank')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'bank' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('bank', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* POS Discount/Premium Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">POS Discount/Premium Account</Label>
                    <p className="text-xs text-muted-foreground">Account for POS discounts and premiums (e.g., 72110001)</p>
                    <div className="relative">
                      {defaultAccounts.pos_discount_premium_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.pos_discount_premium_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.pos_discount_premium_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('pos_discount_premium')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'pos_discount_premium' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('pos_discount_premium')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'pos_discount_premium' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('pos_discount_premium', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Exchange Rounding Difference Account */}
                  <div className="space-y-2">
                    <Label className="font-medium">Exchange Rounding Difference Account</Label>
                    <p className="text-xs text-muted-foreground">Account for micro-rounding adjustments in vouchers (e.g., 67110001)</p>
                    <div className="relative">
                      {defaultAccounts.exchange_rounding_account_id ? (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                          <span className="font-mono text-sm">{defaultAccounts.exchange_rounding_account_code}</span>
                          <span className="text-sm flex-1 truncate">{defaultAccounts.exchange_rounding_account_name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleClearAccount('exchange_rounding')}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search account..."
                              className="pl-9"
                              value={activeAccountField === 'exchange_rounding' ? accountSearch : ''}
                              onFocus={() => setActiveAccountField('exchange_rounding')}
                              onChange={(e) => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }}
                            />
                          </div>
                          {activeAccountField === 'exchange_rounding' && accountSearchResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {accountSearchResults.map(acc => (
                                <div key={acc.id} className="p-2 hover:bg-muted cursor-pointer flex gap-2" onClick={() => handleSelectAccount('exchange_rounding', acc)}>
                                  <span className="font-mono text-xs">{acc.code}</span>
                                  <span className="text-sm truncate">{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveDefaultAccounts} disabled={defaultAccountsSaving}>
                  {defaultAccountsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Default Accounts
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Barcode Tab */}
        <TabsContent value="barcode" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="w-5 h-5" />
                Barcode Generation Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label>EAN-13 Barcode Prefix (6 digits)</Label>
                  <p className="text-xs text-muted-foreground">
                    This prefix will be used when generating barcodes for items without one.
                    The format is: <code className="bg-muted px-1 rounded">[prefix 6 digits] + [sequence 6 digits] + [check digit]</code> = 13-digit EAN barcode.
                  </p>
                  <div className="flex gap-2 items-center">
                    <Input
                      value={barcodePrefix}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setBarcodePrefix(v);
                      }}
                      placeholder="201007"
                      className="w-40 font-mono text-lg tracking-widest"
                      maxLength={6}
                      data-testid="barcode-prefix-input"
                    />
                    <span className="text-muted-foreground font-mono text-lg">000001</span>
                    <span className="text-muted-foreground text-xs">(+ check digit)</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Example: {barcodePrefix.padEnd(6, '0')}000001{(() => {
                      const d = barcodePrefix.padEnd(6, '0') + '000001';
                      let t = 0;
                      for (let i = 0; i < 12; i++) t += parseInt(d[i]) * (i % 2 === 0 ? 1 : 3);
                      return (10 - (t % 10)) % 10;
                    })()}
                  </p>
                </div>
                <Button onClick={saveBarcodePrefix} disabled={barcodePrefixSaving || barcodePrefix.length !== 6} data-testid="save-barcode-prefix-btn">
                  {barcodePrefixSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Prefix
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Label Options Tab - Prescription Label Options Management */}
        <TabsContent value="label-options" className="space-y-4">
          <LabelOptionsManager currentOrg={currentOrg} />
        </TabsContent>

        {/* Fix Trial Balance Tab - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <TabsContent value="fix-trial-balance" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Scale className="w-5 h-5 text-amber-500" />
                      Fix Trial Balance Errors
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Scan for unbalanced vouchers due to micro-rounding differences and fix them automatically.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={scanTrialBalanceErrors}
                      disabled={trialBalanceLoading || !currentOrg}
                    >
                      {trialBalanceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Scan Vouchers
                    </Button>
                    {trialBalanceErrors.length > 0 && (
                      <Button 
                        variant="default"
                        onClick={fixAllVouchers}
                        disabled={trialBalanceFixingAll || !defaultAccounts.exchange_rounding_account_code}
                      >
                        {trialBalanceFixingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                        Fix All ({trialBalanceErrors.length})
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Account Configuration Check */}
                {!defaultAccounts.exchange_rounding_account_code && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-400">Exchange Rounding Account Not Configured</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Please configure the "Exchange Rounding Difference" account in the <strong>Default Accounts</strong> tab 
                        before fixing vouchers. Use account code <strong>67110001</strong>.
                      </p>
                    </div>
                  </div>
                )}

                {/* Threshold Info */}
                <div className="p-3 rounded-lg bg-muted/30 border">
                  <p className="text-xs text-muted-foreground">
                    <strong>Threshold:</strong> Only vouchers with differences ≤ 0.01 USD and ≤ 100 LBP are considered fixable micro-differences.
                    Larger differences require manual adjustment.
                  </p>
                </div>

                {/* Results */}
                {trialBalanceResult && (
                  <div className={`p-4 rounded-lg border ${trialBalanceResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <p className="text-sm font-medium">
                      {trialBalanceResult.success ? '✓' : '✗'} {trialBalanceResult.message}
                    </p>
                    {trialBalanceResult.voucher_number && (
                      <p className="text-xs text-muted-foreground mt-1">Voucher: {trialBalanceResult.voucher_number}</p>
                    )}
                    {trialBalanceResult.errors && trialBalanceResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-red-400">Errors:</p>
                        {trialBalanceResult.errors.map((err, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground">{err.voucher_number}: {err.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Voucher Table */}
                {trialBalanceErrors.length === 0 && !trialBalanceLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Scale className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No unbalanced vouchers found.</p>
                    <p className="text-xs mt-1">Click "Scan Vouchers" to check for micro-differences.</p>
                  </div>
                ) : trialBalanceErrors.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Date</th>
                            <th className="text-left p-3 font-medium">Voucher #</th>
                            <th className="text-left p-3 font-medium">Description</th>
                            <th className="text-right p-3 font-medium">USD Diff</th>
                            <th className="text-right p-3 font-medium">LBP Diff</th>
                            <th className="text-center p-3 font-medium">Status</th>
                            <th className="text-center p-3 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {trialBalanceErrors.map(v => (
                            <tr key={v.id} className="hover:bg-muted/30">
                              <td className="p-3 font-mono text-xs">{v.date}</td>
                              <td className="p-3 font-mono text-cyan-400">{v.voucher_number}</td>
                              <td className="p-3 truncate max-w-[200px]" title={v.description}>{v.description || '-'}</td>
                              <td className="p-3 text-right font-mono">
                                <span className={v.diff_usd > 0 ? 'text-red-400' : v.diff_usd < 0 ? 'text-green-400' : ''}>
                                  {v.diff_usd > 0 ? '+' : ''}{v.diff_usd.toFixed(2)}
                                </span>
                              </td>
                              <td className="p-3 text-right font-mono">
                                <span className={v.diff_lbp > 0 ? 'text-red-400' : v.diff_lbp < 0 ? 'text-green-400' : ''}>
                                  {v.diff_lbp > 0 ? '+' : ''}{v.diff_lbp.toLocaleString()}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                {v.is_posted ? (
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">Posted</span>
                                ) : (
                                  <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">Draft</span>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => fixSingleVoucher(v.id)}
                                  disabled={trialBalanceFixing === v.id || !defaultAccounts.exchange_rounding_account_code}
                                >
                                  {trialBalanceFixing === v.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Wrench className="w-4 h-4" />
                                  )}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scan for Missing Accounts Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Scan for Missing Accounts
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Check if all account codes used in vouchers exist in the Chart of Accounts.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={scanOrphanAccounts}
                    disabled={orphanAccountsLoading || !currentOrg}
                  >
                    {orphanAccountsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Scan Accounts
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                {orphanAccountsStats && (
                  <div className="p-3 rounded-lg bg-muted/30 border">
                    <p className="text-xs text-muted-foreground">
                      Scanned <strong>{orphanAccountsStats.total_vouchers.toLocaleString()}</strong> vouchers against{' '}
                      <strong>{orphanAccountsStats.total_accounts.toLocaleString()}</strong> accounts in Chart of Accounts.
                    </p>
                  </div>
                )}

                {/* Results */}
                {orphanAccounts.length === 0 && !orphanAccountsLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No missing accounts found.</p>
                    <p className="text-xs mt-1">Click "Scan Accounts" to check for orphan account codes.</p>
                  </div>
                ) : orphanAccounts.length > 0 && (
                  <>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-400">
                          {orphanAccounts.length} Missing Account{orphanAccounts.length > 1 ? 's' : ''} Found
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          These account codes are used in vouchers but don't exist in the Chart of Accounts.
                          You can create them manually in Chart of Accounts.
                        </p>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 font-medium">Account Code</th>
                              <th className="text-left p-3 font-medium">Used in Vouchers</th>
                              <th className="text-center p-3 font-medium">Count</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {orphanAccounts.map((acc, idx) => (
                              <tr key={idx} className="hover:bg-muted/30">
                                <td className="p-3 font-mono text-red-400 font-medium">{acc.account_code}</td>
                                <td className="p-3">
                                  <div className="flex flex-wrap gap-1">
                                    {acc.vouchers.slice(0, 5).map((v, vidx) => (
                                      <span key={vidx} className="text-xs px-2 py-0.5 rounded bg-muted font-mono">
                                        {v.voucher_number}
                                      </span>
                                    ))}
                                    {acc.voucher_count > 5 && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-muted/50 text-muted-foreground">
                                        +{acc.voucher_count - 5} more
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 font-medium">
                                    {acc.voucher_count}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Account Balance Reconciliation Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Calculator className="w-5 h-5 text-cyan-500" />
                      Account Balance Reconciliation
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Compare stored account balances vs calculated from voucher entries.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={scanBalanceDiscrepancies}
                      disabled={balanceReconcileLoading || !currentOrg}
                    >
                      {balanceReconcileLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Scan Balances
                    </Button>
                    {balanceDiscrepancies.length > 0 && (
                      <Button 
                        variant="default"
                        onClick={recalculateAllBalances}
                        disabled={balanceReconcileFixingAll}
                      >
                        {balanceReconcileFixingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                        Fix All ({balanceDiscrepancies.length})
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Voucher Totals from scan */}
                {balanceReconcileStats?.voucher_totals && (
                  <div className="p-3 rounded-lg bg-muted/30 border">
                    <p className="text-xs font-medium mb-2">Voucher Entry Totals (should be balanced):</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Debit USD:</span>
                        <span className="ml-1 font-mono text-emerald-400">${balanceReconcileStats.voucher_totals.debit_usd.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Credit USD:</span>
                        <span className="ml-1 font-mono text-red-400">${balanceReconcileStats.voucher_totals.credit_usd.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Diff USD:</span>
                        <span className={`ml-1 font-mono ${balanceReconcileStats.voucher_totals.diff_usd === 0 ? 'text-green-400' : 'text-amber-400'}`}>
                          ${balanceReconcileStats.voucher_totals.diff_usd.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Diff LBP:</span>
                        <span className={`ml-1 font-mono ${balanceReconcileStats.voucher_totals.diff_lbp === 0 ? 'text-green-400' : 'text-amber-400'}`}>
                          {balanceReconcileStats.voucher_totals.diff_lbp.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results */}
                {balanceDiscrepancies.length === 0 && !balanceReconcileLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No balance discrepancies found.</p>
                    <p className="text-xs mt-1">Click "Scan Balances" to check account balances against voucher entries.</p>
                  </div>
                ) : balanceDiscrepancies.length > 0 && (
                  <>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-400">
                          {balanceDiscrepancies.length} Account{balanceDiscrepancies.length > 1 ? 's' : ''} with Balance Discrepancies
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          These accounts have stored balances that don't match the sum of voucher entries.
                          Click "Fix" to recalculate from voucher entries.
                        </p>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 font-medium">Account</th>
                              <th className="text-right p-3 font-medium">Stored USD</th>
                              <th className="text-right p-3 font-medium">Calculated USD</th>
                              <th className="text-right p-3 font-medium">Diff USD</th>
                              <th className="text-right p-3 font-medium">Diff LBP</th>
                              <th className="text-center p-3 font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {balanceDiscrepancies.map((d, idx) => (
                              <tr key={idx} className="hover:bg-muted/30">
                                <td className="p-3">
                                  <span className="font-mono text-cyan-400">{d.account_code}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">{d.account_name}</span>
                                </td>
                                <td className="p-3 text-right font-mono">${d.stored_balance_usd?.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono">${d.calculated_balance_usd?.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono">
                                  <span className={d.diff_usd > 0 ? 'text-red-400' : 'text-green-400'}>
                                    {d.diff_usd > 0 ? '+' : ''}{d.diff_usd?.toLocaleString()}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-mono">
                                  <span className={d.diff_lbp > 0 ? 'text-red-400' : 'text-green-400'}>
                                    {d.diff_lbp > 0 ? '+' : ''}{d.diff_lbp?.toLocaleString()}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => recalculateSingleBalance(d.account_id)}
                                    disabled={balanceReconcileFixing === d.account_id}
                                  >
                                    {balanceReconcileFixing === d.account_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Wrench className="w-4 h-4" />
                                    )}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Transfer Account Transactions Card */}
            <Card>
              <CardHeader className="pb-3">
                <div>
                  <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <ArrowRightLeft className="w-5 h-5 text-purple-500" />
                    Transfer Account Transactions
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Move all transactions from one account to another. Useful for migrating old account codes.
                  </p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Source and Target Account Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Source Account Code</Label>
                    <Input
                      placeholder="e.g., 442"
                      value={transferSourceCode}
                      onChange={(e) => { setTransferSourceCode(e.target.value); setTransferPreview(null); }}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">Account to transfer FROM (can be old/orphan code)</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Target Account Code</Label>
                    <Input
                      placeholder="e.g., 44270001"
                      value={transferTargetCode}
                      onChange={(e) => { setTransferTargetCode(e.target.value); setTransferPreview(null); }}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">Account to transfer TO (must exist in CoA)</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={previewAccountTransfer}
                    disabled={transferPreviewLoading || !transferSourceCode || !transferTargetCode}
                  >
                    {transferPreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview Transfer
                  </Button>
                </div>

                {/* Preview Results */}
                {transferPreview && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/20">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Info className="w-4 h-4 text-cyan-400" />
                      Transfer Preview
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {/* Source Account */}
                      <div className="p-3 rounded border bg-red-500/5 border-red-500/20">
                        <p className="text-xs text-muted-foreground mb-1">FROM (Source)</p>
                        <p className="font-mono text-red-400">{transferPreview.source_account.code}</p>
                        <p className="text-xs">{transferPreview.source_account.name}</p>
                        {!transferPreview.source_account.exists_in_coa && (
                          <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 mt-1 inline-block">
                            Not in CoA (orphan)
                          </span>
                        )}
                      </div>
                      
                      {/* Target Account */}
                      <div className="p-3 rounded border bg-green-500/5 border-green-500/20">
                        <p className="text-xs text-muted-foreground mb-1">TO (Target)</p>
                        <p className="font-mono text-green-400">{transferPreview.target_account.code}</p>
                        <p className="text-xs">{transferPreview.target_account.name}</p>
                      </div>
                    </div>

                    {/* Impact Summary */}
                    <div className="p-3 rounded border bg-muted/30">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Transfer Impact:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Vouchers:</span>
                          <span className="ml-1 font-mono font-medium">{transferPreview.impact.vouchers_affected}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Lines:</span>
                          <span className="ml-1 font-mono font-medium">{transferPreview.impact.lines_affected}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Net USD:</span>
                          <span className="ml-1 font-mono">${transferPreview.impact.net_usd?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Net LBP:</span>
                          <span className="ml-1 font-mono">{transferPreview.impact.net_lbp?.toLocaleString()}</span>
                        </div>
                      </div>
                      {transferPreview.sample_vouchers?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/50">
                          <p className="text-xs text-muted-foreground">Sample vouchers: {transferPreview.sample_vouchers.join(', ')}{transferPreview.has_more_vouchers ? '...' : ''}</p>
                        </div>
                      )}
                    </div>

                    {/* Warning and Execute Button */}
                    {transferPreview.impact.lines_affected > 0 ? (
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2 text-amber-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs">This action cannot be undone!</span>
                        </div>
                        <Button
                          variant="destructive"
                          onClick={executeAccountTransfer}
                          disabled={transferExecuting}
                        >
                          {transferExecuting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                          Execute Transfer
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        <p className="text-sm">No transactions found for account {transferSourceCode}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Exchange Rate Validation Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <TrendingUp className="w-5 h-5 text-orange-500" />
                      Scan Exchange Rate Errors
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Find transactions where LBP/USD ratio doesn't match valid exchange rates.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Config Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Valid Exchange Rates</Label>
                    <Input
                      placeholder="89500,8900"
                      value={exchangeRateValidRates}
                      onChange={(e) => setExchangeRateValidRates(e.target.value)}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">Comma-separated</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tolerance (±LBP)</Label>
                    <Input
                      type="number"
                      placeholder="750"
                      value={exchangeRateTolerance}
                      onChange={(e) => setExchangeRateTolerance(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Correct Rate (for Fix All)</Label>
                    <Input
                      type="number"
                      placeholder="89500"
                      value={exchangeRateCorrectRate}
                      onChange={(e) => setExchangeRateCorrectRate(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={scanExchangeRateErrors}
                    disabled={exchangeRateLoading || !currentOrg}
                  >
                    {exchangeRateLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Scan Rates
                  </Button>
                  {exchangeRateErrors.length > 0 && (
                    <Button
                      variant="default"
                      onClick={fixAllExchangeRates}
                      disabled={exchangeRateFixingAll}
                    >
                      {exchangeRateFixingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                      Fix All ({exchangeRateErrors.length})
                    </Button>
                  )}
                </div>

                {/* Stats */}
                {exchangeRateStats && (
                  <div className="p-3 rounded-lg bg-muted/30 border">
                    <p className="text-xs text-muted-foreground">
                      Scanned <strong>{exchangeRateStats.total_vouchers.toLocaleString()}</strong> vouchers.
                      {exchangeRateStats.has_more && ' Showing first 200 errors.'}
                    </p>
                  </div>
                )}

                {/* Results */}
                {exchangeRateErrors.length === 0 && !exchangeRateLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No exchange rate errors found.</p>
                    <p className="text-xs mt-1">Click "Scan Rates" to check for incorrect LBP/USD ratios.</p>
                  </div>
                ) : exchangeRateErrors.length > 0 && (
                  <>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
                      <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-400">
                          {exchangeRateErrors.length} Transaction{exchangeRateErrors.length > 1 ? 's' : ''} with Incorrect Exchange Rate
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          These lines have LBP amounts that don't match expected USD × rate.
                        </p>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[500px]">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 font-medium">Voucher</th>
                              <th className="text-left p-3 font-medium">Account</th>
                              <th className="text-right p-3 font-medium">USD</th>
                              <th className="text-right p-3 font-medium">LBP</th>
                              <th className="text-right p-3 font-medium">Actual Rate</th>
                              <th className="text-right p-3 font-medium">Expected</th>
                              <th className="text-center p-3 font-medium">Fix</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {exchangeRateErrors.map((err, idx) => (
                              <tr key={idx} className="hover:bg-muted/30">
                                <td className="p-3">
                                  <span className="font-mono text-cyan-400">{err.voucher_number}</span>
                                  <span className="text-xs text-muted-foreground ml-1">({err.date})</span>
                                </td>
                                <td className="p-3 font-mono text-xs">{err.account_code}</td>
                                <td className="p-3 text-right font-mono">${err.usd_amount?.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono">{err.lbp_amount?.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono text-red-400">{err.effective_rate?.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono text-green-400">{err.nearest_valid_rate?.toLocaleString()}</td>
                                <td className="p-3 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => fixSingleExchangeRate(err.voucher_id, err.line_index, err.nearest_valid_rate)}
                                    disabled={exchangeRateFixing === `${err.voucher_id}-${err.line_index}`}
                                    title={`Fix using rate ${err.nearest_valid_rate}`}
                                  >
                                    {exchangeRateFixing === `${err.voucher_id}-${err.line_index}` ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Wrench className="w-4 h-4" />
                                    )}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {exchangeRateStats?.has_more && (
                        <div className="p-3 text-center text-xs text-muted-foreground border-t">
                          Showing {exchangeRateErrors.length} errors. More errors exist - use "Fix All" to correct all.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Account Transaction Audit Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Search className="w-5 h-5 text-purple-500" />
                  Account Transaction Audit
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Trace all transactions for a specific account to identify LBP discrepancies against expected exchange rate.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Account Code</Label>
                    <Input
                      value={auditAccountCode}
                      onChange={(e) => setAuditAccountCode(e.target.value)}
                      placeholder="e.g., 110001"
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Expected Rate (LBP/USD)</Label>
                    <Input
                      type="number"
                      value={auditExpectedRate}
                      onChange={(e) => setAuditExpectedRate(e.target.value)}
                      placeholder="89500"
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={auditAccount}
                      disabled={auditLoading || !auditAccountCode}
                      className="w-full"
                    >
                      {auditLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Audit Account
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Audit Results */}
                {auditResult && (
                  <>
                    {/* Account Info & Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="p-4 rounded-lg bg-muted/30 border">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Account Info
                        </h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-muted-foreground">Code:</span> <span className="font-mono">{auditResult.account?.code}</span></p>
                          <p><span className="text-muted-foreground">Name:</span> {auditResult.account?.name}</p>
                          <p><span className="text-muted-foreground">Stored USD:</span> <span className="font-mono">${auditResult.account?.stored_balance_usd?.toLocaleString()}</span></p>
                          <p><span className="text-muted-foreground">Stored LBP:</span> <span className="font-mono">{auditResult.account?.stored_balance_lbp?.toLocaleString()} LBP</span></p>
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/30 border">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Calculator className="w-4 h-4" />
                          Calculated Summary
                        </h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-muted-foreground">Total Transactions:</span> <span className="font-mono">{auditResult.summary?.total_transactions}</span></p>
                          <p><span className="text-muted-foreground">Calculated USD:</span> <span className="font-mono">${auditResult.summary?.calculated_balance_usd?.toLocaleString()}</span></p>
                          <p><span className="text-muted-foreground">Calculated LBP:</span> <span className="font-mono">{auditResult.summary?.calculated_balance_lbp?.toLocaleString()} LBP</span></p>
                          <p><span className="text-muted-foreground">Expected LBP (at rate {auditResult.summary?.expected_rate}):</span> <span className="font-mono">{auditResult.summary?.expected_balance_lbp?.toLocaleString()} LBP</span></p>
                        </div>
                      </div>
                    </div>

                    {/* Discrepancy Alert */}
                    {auditResult.summary?.errors_count > 0 && (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                        <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-orange-400">
                            {auditResult.summary?.errors_count} Transaction{auditResult.summary?.errors_count > 1 ? 's' : ''} with LBP Discrepancy
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Total discrepancy: <span className="font-mono text-orange-300">{auditResult.summary?.total_lbp_discrepancy?.toLocaleString()} LBP</span>
                          </p>
                        </div>
                      </div>
                    )}

                    {auditResult.summary?.errors_count === 0 && (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                        <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                        <p className="text-sm text-green-400">All transactions match the expected exchange rate (±1000 LBP tolerance).</p>
                      </div>
                    )}

                    {/* Toggle: Show All vs Errors Only */}
                    {auditResult.error_transactions?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant={!auditShowAll ? "default" : "outline"}
                          size="sm"
                          onClick={() => setAuditShowAll(false)}
                        >
                          Errors Only ({auditResult.error_transactions?.length})
                        </Button>
                        <Button
                          variant={auditShowAll ? "default" : "outline"}
                          size="sm"
                          onClick={() => setAuditShowAll(true)}
                        >
                          All Transactions ({auditResult.all_transactions?.length})
                        </Button>
                      </div>
                    )}

                    {/* Transaction Table */}
                    {((auditShowAll ? auditResult.all_transactions : auditResult.error_transactions) || []).length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto max-h-[500px]">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-left p-2 font-medium">Voucher</th>
                                <th className="text-left p-2 font-medium">Date</th>
                                <th className="text-right p-2 font-medium">Net USD</th>
                                <th className="text-right p-2 font-medium">Net LBP</th>
                                <th className="text-right p-2 font-medium">Expected LBP</th>
                                <th className="text-right p-2 font-medium">Difference</th>
                                <th className="text-right p-2 font-medium">Running USD</th>
                                <th className="text-right p-2 font-medium">Running LBP</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {(auditShowAll ? auditResult.all_transactions : auditResult.error_transactions).map((t, idx) => (
                                <tr key={idx} className={`hover:bg-muted/30 ${t.is_error ? 'bg-red-500/5' : ''}`}>
                                  <td className="p-2">
                                    <span className="font-mono text-cyan-400">{t.voucher_number}</span>
                                  </td>
                                  <td className="p-2 text-xs text-muted-foreground">{t.date}</td>
                                  <td className="p-2 text-right font-mono">
                                    <span className={t.net_usd >= 0 ? 'text-green-400' : 'text-red-400'}>
                                      {t.net_usd >= 0 ? '+' : ''}{t.net_usd?.toLocaleString()}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono">
                                    <span className={t.net_lbp >= 0 ? 'text-green-400' : 'text-red-400'}>
                                      {t.net_lbp >= 0 ? '+' : ''}{t.net_lbp?.toLocaleString()}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono text-muted-foreground">
                                    {t.expected_lbp?.toLocaleString()}
                                  </td>
                                  <td className="p-2 text-right font-mono">
                                    <span className={Math.abs(t.lbp_difference) > 1000 ? 'text-orange-400 font-semibold' : 'text-muted-foreground'}>
                                      {t.lbp_difference >= 0 ? '+' : ''}{t.lbp_difference?.toLocaleString()}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono text-blue-400">
                                    {t.running_balance_usd?.toLocaleString()}
                                  </td>
                                  <td className="p-2 text-right font-mono text-blue-400">
                                    {t.running_balance_lbp?.toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Branding Tab - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <TabsContent value="branding" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Sparkles className="w-5 h-5 text-emerald-500" />
                  Version & Branding
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Configure the app version badge and logo displayed in the footer.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Preview */}
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <Label className="text-xs text-muted-foreground mb-2 block">Footer Preview (Right-aligned)</Label>
                  <div className="flex items-center justify-end gap-3 py-3">
                    {brandingSettings.logo_url ? (
                      <img 
                        src={brandingSettings.logo_url.startsWith('http') ? brandingSettings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brandingSettings.logo_url}`} 
                        alt="Logo" 
                        className="h-8 w-auto object-contain"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <span className="text-white text-sm font-bold">K</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">KAIROS</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-medium">
                        v{brandingSettings.version || '2.5'}
                        {brandingSettings.label && (
                          <span className="ml-1 text-emerald-500">{brandingSettings.label}</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Version Input */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Version Number</Label>
                    <Input 
                      value={brandingSettings.version || ''}
                      onChange={(e) => setBrandingSettings(prev => ({ ...prev, version: e.target.value }))}
                      placeholder="e.g., 2.5"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">The version number (e.g., 2.5, 3.0.1)</p>
                  </div>
                  <div>
                    <Label className="text-sm">Version Label (Optional)</Label>
                    <Input 
                      value={brandingSettings.label || ''}
                      onChange={(e) => setBrandingSettings(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g., Beta, Pro, LTS"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Optional label like "Beta", "Pro"</p>
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <Label className="text-sm">Footer Logo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-muted-foreground/30">
                      {brandingSettings.logo_url ? (
                        <img 
                          src={brandingSettings.logo_url.startsWith('http') ? brandingSettings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brandingSettings.logo_url}`} 
                          alt="Logo" 
                          className="w-full h-full object-contain rounded-lg"
                        />
                      ) : (
                        <Sparkles className="w-6 h-6 text-muted-foreground/50" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            setBrandingSaving(true);
                            const response = await axios.post(
                              `${API}/organizations/${currentOrg?.id}/version-logo`,
                              formData,
                              { headers: { 'Content-Type': 'multipart/form-data' } }
                            );
                            setBrandingSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
                            toast.success('Logo uploaded successfully!');
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to upload logo');
                          } finally {
                            setBrandingSaving(false);
                          }
                        }}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        disabled={brandingSaving}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </Button>
                      {brandingSettings.logo_url && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-400"
                          onClick={() => setBrandingSettings(prev => ({ ...prev, logo_url: null }))}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Recommended: PNG or SVG, max 200x50px</p>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={async () => {
                      try {
                        setBrandingSaving(true);
                        await axios.put(`${API}/organizations/${currentOrg?.id}/version-settings`, brandingSettings);
                        toast.success('Branding settings saved!');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to save settings');
                      } finally {
                        setBrandingSaving(false);
                      }
                    }}
                    disabled={brandingSaving}
                  >
                    {brandingSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Branding Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Tools Tab - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <TabsContent value="tools" className="space-y-4">
            <Card className="border-red-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-red-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <TrashIcon className="w-5 h-5" />
                  Bulk Delete Inventory Items
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Delete inventory items created or updated after a specific date/time. <strong className="text-red-400">IRREVERSIBLE!</strong>
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Delete items where:</Label>
                    <Select value={bulkDeleteType} onValueChange={setBulkDeleteType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="updated">Updated At</SelectItem>
                        <SelectItem value="created">Created At</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Is after date:</Label>
                    <Input 
                      type="date" 
                      value={bulkDeleteDate}
                      onChange={(e) => setBulkDeleteDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Is after time:</Label>
                    <Input 
                      type="time" 
                      value={bulkDeleteTime}
                      onChange={(e) => setBulkDeleteTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleBulkDeletePreview}
                    disabled={!bulkDeleteDate || !bulkDeleteTime || bulkDeleteLoading}
                  >
                    {bulkDeleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Preview
                  </Button>
                </div>

                {bulkDeletePreview && (
                  <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          Found <span className="text-red-500 font-bold">{bulkDeletePreview.count}</span> items
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Where {bulkDeletePreview.date_field} &gt;= {bulkDeletePreview.after_datetime}
                        </p>
                      </div>
                      {bulkDeletePreview.count > 0 && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => setBulkDeleteConfirmOpen(true)}
                        >
                          <TrashIcon className="w-4 h-4 mr-2" />
                          Delete {bulkDeletePreview.count} Items
                        </Button>
                      )}
                    </div>
                    
                    {bulkDeletePreview.sample && bulkDeletePreview.sample.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium mb-1">Sample items to be deleted:</p>
                        <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                          {bulkDeletePreview.sample.map((item, idx) => (
                            <div key={idx} className="flex gap-2 text-muted-foreground">
                              <span className="font-mono text-cyan-400">{item.item_code}</span>
                              <span className="truncate">{item.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {bulkDeleteResult && (
                  <div className={`border rounded-lg p-4 ${bulkDeleteResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <p className="text-sm font-medium">
                      {bulkDeleteResult.success 
                        ? `✓ Successfully deleted ${bulkDeleteResult.deleted_count} items`
                        : `✗ Error: ${bulkDeleteResult.error}`
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* About / Version Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Info className="w-5 h-5 text-blue-400" />
                  About Kairos ERP
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Version</p>
                    <p className="text-lg font-bold text-blue-400">v{APP_VERSION.version}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Release Date</p>
                    <p className="text-sm font-medium">{new Date(APP_VERSION.releaseDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at {APP_VERSION.releaseTime}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground mb-2">What's New in This Version</p>
                  <div className="bg-muted/30 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <ul className="text-xs space-y-1">
                      {APP_VERSION.changelog.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">•</span>
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <p className="text-[10px] text-muted-foreground text-center">
                    Kairos POS • Built with ❤️ for {currentOrg?.name || 'Your Business'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* AI Integration Tab - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <TabsContent value="ai-integration" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Bot className="w-5 h-5 text-purple-500" />
                  AI Integration Settings
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Configure your Claude AI API key for medicine information, drug interactions, and supplier analysis features.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Status */}
                <div className={`p-4 rounded-lg border ${currentOrg?.has_claude_api_key ? 'bg-green-500/10 border-green-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {currentOrg?.has_claude_api_key ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium text-green-600 dark:text-green-400">Claude API Key Configured</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        <span className="font-medium text-orange-600 dark:text-orange-400">Claude API Key Not Configured</span>
                      </>
                    )}
                  </div>
                  {currentOrg?.has_claude_api_key && currentOrg?.claude_api_key_masked && (
                    <p className="text-sm font-mono text-muted-foreground">
                      Current Key: {currentOrg.claude_api_key_masked}
                    </p>
                  )}
                </div>

                {/* API Key Input */}
                <div className="space-y-2">
                  <Label htmlFor="claude-api-key">Claude API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      id="claude-api-key"
                      type="password"
                      placeholder="sk-ant-api03-..."
                      value={claudeApiKey}
                      onChange={(e) => setClaudeApiKey(e.target.value)}
                      className="font-mono"
                    />
                    <Button
                      onClick={handleSaveClaudeApiKey}
                      disabled={!claudeApiKey || savingClaudeKey}
                    >
                      {savingClaudeKey ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Key
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get your API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Anthropic Console</a>
                  </p>
                </div>

                {/* Test API Key */}
                <div className="space-y-2">
                  <Label>Test AI Integration</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleTestClaudeApi}
                      disabled={testingClaudeApi || !currentOrg?.has_claude_api_key}
                    >
                      {testingClaudeApi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Test Connection
                        </>
                      )}
                    </Button>
                  </div>
                  {claudeTestResult && (
                    <div className={`p-3 rounded-lg text-sm ${claudeTestResult.success ? 'bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400'}`}>
                      {claudeTestResult.message}
                    </div>
                  )}
                </div>

                {/* AI Features Using This Key */}
                <div className="space-y-2">
                  <Label>AI Features Using This Key</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <div className="flex items-center gap-2 mb-1">
                        <Pill className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Medicine Info</span>
                      </div>
                      <p className="text-xs text-muted-foreground">POS & Inventory - Get detailed drug information in EN/AR/FR</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium">Drug Interactions</span>
                      </div>
                      <p className="text-xs text-muted-foreground">POS - Check for dangerous drug combinations</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium">Supplier Analysis</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Purchase Orders - AI insights on supplier performance</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergent LLM Key Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Emergent LLM Key (Fallback AI)
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Alternative AI key used when Claude API key is not configured.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Status */}
                <div className={`p-4 rounded-lg border ${currentOrg?.has_emergent_llm_key ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-500/10 border-gray-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {currentOrg?.has_emergent_llm_key ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium text-green-600 dark:text-green-400">Emergent LLM Key Configured</span>
                      </>
                    ) : (
                      <>
                        <Info className="w-5 h-5 text-gray-500" />
                        <span className="font-medium text-gray-600 dark:text-gray-400">Emergent LLM Key Not Configured</span>
                      </>
                    )}
                  </div>
                  {currentOrg?.has_emergent_llm_key && currentOrg?.emergent_llm_key_masked && (
                    <p className="text-sm font-mono text-muted-foreground">
                      Current Key: {currentOrg.emergent_llm_key_masked}
                    </p>
                  )}
                </div>

                {/* Key Input */}
                <div className="space-y-2">
                  <Label htmlFor="emergent-llm-key">Emergent LLM Key</Label>
                  <div className="flex gap-2">
                    <Input
                      id="emergent-llm-key"
                      type="password"
                      placeholder="ek-..."
                      value={emergentLlmKey}
                      onChange={(e) => setEmergentLlmKey(e.target.value)}
                      className="font-mono"
                    />
                    <Button
                      onClick={handleSaveEmergentLlmKey}
                      disabled={!emergentLlmKey || savingEmergentKey}
                    >
                      {savingEmergentKey ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AWS/S3 Configuration Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Cloud className="w-5 h-5 text-orange-500" />
                  AWS S3 Configuration
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Configure AWS credentials for image storage (inventory images, logos, documents).
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Status */}
                <div className={`p-4 rounded-lg border ${currentOrg?.has_aws_credentials ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-500/10 border-gray-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {currentOrg?.has_aws_credentials ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium text-green-600 dark:text-green-400">AWS Credentials Configured</span>
                      </>
                    ) : (
                      <>
                        <Info className="w-5 h-5 text-gray-500" />
                        <span className="font-medium text-gray-600 dark:text-gray-400">AWS Credentials Not Configured</span>
                      </>
                    )}
                  </div>
                  {currentOrg?.has_aws_credentials && currentOrg?.aws_access_key_id_masked && (
                    <p className="text-sm font-mono text-muted-foreground">
                      Access Key ID: {currentOrg.aws_access_key_id_masked}
                    </p>
                  )}
                  {currentOrg?.aws_region && (
                    <p className="text-sm text-muted-foreground">
                      Region: {currentOrg.aws_region}
                    </p>
                  )}
                  {currentOrg?.s3_bucket_name && (
                    <p className="text-sm text-muted-foreground">
                      Bucket: {currentOrg.s3_bucket_name}
                    </p>
                  )}
                </div>

                {/* AWS Credentials Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="aws-access-key">AWS Access Key ID</Label>
                    <Input
                      id="aws-access-key"
                      type="password"
                      placeholder="AKIA..."
                      value={awsAccessKeyId}
                      onChange={(e) => setAwsAccessKeyId(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aws-secret-key">AWS Secret Access Key</Label>
                    <Input
                      id="aws-secret-key"
                      type="password"
                      placeholder="Enter secret key..."
                      value={awsSecretAccessKey}
                      onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aws-region">AWS Region</Label>
                    <Input
                      id="aws-region"
                      type="text"
                      placeholder="eu-north-1"
                      value={awsRegion}
                      onChange={(e) => setAwsRegion(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="s3-bucket">S3 Bucket Name</Label>
                    <Input
                      id="s3-bucket"
                      type="text"
                      placeholder="my-pharmacy-files"
                      value={s3BucketName}
                      onChange={(e) => setS3BucketName(e.target.value)}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveAwsConfig}
                  disabled={savingAwsConfig || (!awsAccessKeyId && !awsSecretAccessKey && !awsRegion && !s3BucketName)}
                  className="w-full"
                >
                  {savingAwsConfig ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save AWS Configuration
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground">
                  Get your AWS credentials from <a href="https://console.aws.amazon.com/iam/home#/security_credentials" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">AWS IAM Console</a>. 
                  Your S3 bucket must have appropriate CORS settings for direct uploads.
                </p>
              </CardContent>
            </Card>

            {/* ============= AI / STOCK IMAGE PROVIDERS ============= */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Image Providers — AI & Stock Photos
                </CardTitle>
                <CardDescription>
                  Provide your own API keys to (1) auto-generate cartoon item images with <b>OpenAI gpt-image-1</b>,
                  and (2) fetch high-quality stock photos from <b>Unsplash</b> or <b>Pexels</b>.
                  Keys are stored per-organization in MongoDB and never exposed to the browser.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    {currentOrg?.has_openai_api_key ? (
                      <span className="text-xs text-green-600 dark:text-green-400">✓ OpenAI key configured ({currentOrg.openai_api_key_masked})</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No OpenAI key yet</span>
                    )}
                  </div>
                  <Label className="text-xs">OpenAI API Key <span className="text-muted-foreground">(starts with sk-…)</span></Label>
                  <Input
                    type="password"
                    placeholder="sk-..."
                    value={openaiApiKey}
                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                    data-testid="openai-key-input"
                  />
                  <p className="text-[11px] text-muted-foreground">Get it from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">platform.openai.com/api-keys</a>. Your account may need to be verified to use gpt-image-1.</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    {currentOrg?.has_unsplash_access_key ? (
                      <span className="text-xs text-green-600 dark:text-green-400">✓ Unsplash key configured ({currentOrg.unsplash_access_key_masked})</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No Unsplash key yet</span>
                    )}
                  </div>
                  <Label className="text-xs">Unsplash Access Key <span className="text-muted-foreground">(free, 50 req/hour)</span></Label>
                  <Input
                    type="password"
                    placeholder="Unsplash Access Key"
                    value={unsplashAccessKey}
                    onChange={(e) => setUnsplashAccessKey(e.target.value)}
                    data-testid="unsplash-key-input"
                  />
                  <p className="text-[11px] text-muted-foreground">Register an app at <a href="https://unsplash.com/oauth/applications" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">unsplash.com/oauth/applications</a>.</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    {currentOrg?.has_pexels_api_key ? (
                      <span className="text-xs text-green-600 dark:text-green-400">✓ Pexels key configured ({currentOrg.pexels_api_key_masked})</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No Pexels key yet</span>
                    )}
                  </div>
                  <Label className="text-xs">Pexels API Key <span className="text-muted-foreground">(free, 200 req/hour)</span></Label>
                  <Input
                    type="password"
                    placeholder="Pexels API Key"
                    value={pexelsApiKey}
                    onChange={(e) => setPexelsApiKey(e.target.value)}
                    data-testid="pexels-key-input"
                  />
                  <p className="text-[11px] text-muted-foreground">Get yours at <a href="https://www.pexels.com/api/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">pexels.com/api</a>.</p>
                </div>

                <Button
                  onClick={async () => {
                    if (!openaiApiKey.trim() && !unsplashAccessKey.trim() && !pexelsApiKey.trim()) {
                      alert('Enter at least one key to save');
                      return;
                    }
                    setSavingImageKeys(true);
                    try {
                      const updateData = {};
                      if (openaiApiKey.trim()) updateData.openai_api_key = openaiApiKey.trim();
                      if (unsplashAccessKey.trim()) updateData.unsplash_access_key = unsplashAccessKey.trim();
                      if (pexelsApiKey.trim()) updateData.pexels_api_key = pexelsApiKey.trim();
                      await axios.put(`${API}/organizations/${currentOrg.id}`, updateData);
                      await fetchOrganizations();
                      setOpenaiApiKey(''); setUnsplashAccessKey(''); setPexelsApiKey('');
                      alert('Image provider keys saved.');
                    } catch (err) {
                      alert(err?.response?.data?.detail || 'Failed to save keys');
                    } finally { setSavingImageKeys(false); }
                  }}
                  disabled={savingImageKeys || (!openaiApiKey && !unsplashAccessKey && !pexelsApiKey)}
                  data-testid="save-image-keys-btn"
                >
                  {savingImageKeys ? 'Saving...' : (<><Save className="w-4 h-4 mr-2"/>Save Image Provider Keys</>)}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreConfirmOpen} onOpenChange={(open) => { setRestoreConfirmOpen(open); if (!open) { setRestoreFile(null); if (restoreFileRef.current) restoreFileRef.current.value = ''; } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm Restore
            </DialogTitle>
            <DialogDescription>
              You are about to restore from: <strong>{restoreFile?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Restore Mode</Label>
              <Select value={restoreMode} onValueChange={setRestoreMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge (Add new, update existing)</SelectItem>
                  <SelectItem value="replace">Replace (Clear and restore)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {restoreMode === 'merge' 
                  ? 'Existing records will be updated, new records will be added.'
                  : 'Warning: This will delete existing data before restoring!'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreConfirmOpen(false); setRestoreFile(null); }}>
              Cancel
            </Button>
            <Button 
              variant={restoreMode === 'replace' ? 'destructive' : 'default'}
              onClick={() => { setRestoreConfirmOpen(false); handleRestoreBackup(); }}
              disabled={restoreLoading}
            >
              {restoreLoading ? 'Restoring...' : 'Restore Backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.name}</strong> ({deleteConfirm?.email})?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Organization Confirmation */}
      <Dialog open={!!deleteOrgConfirm} onOpenChange={() => { setDeleteOrgConfirm(null); setDeleteOrgForce(false); setDeleteOrgError(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteOrgConfirm?.name}</strong>?
              {!deleteOrgForce && (
                <span className="block mt-2 text-muted-foreground">
                  This will check if the organization has any data before deleting.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {deleteOrgError && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <p className="text-amber-400 text-sm font-medium mb-2">⚠️ Organization has existing data:</p>
              <p className="text-sm text-muted-foreground">{deleteOrgError}</p>
              <p className="text-sm text-red-400 mt-2 font-medium">
                Click "Force Delete" to permanently delete the organization and ALL its data.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteOrgConfirm(null); setDeleteOrgForce(false); setDeleteOrgError(null); }}>Cancel</Button>
            {deleteOrgForce ? (
              <Button variant="destructive" onClick={() => handleDeleteOrg(true)}>
                Force Delete All Data
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => handleDeleteOrg(false)}>Delete</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Currency Confirmation */}
      <Dialog open={!!deleteCurrencyConfirm} onOpenChange={() => setDeleteCurrencyConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Currency</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteCurrencyConfirm?.code} - {deleteCurrencyConfirm?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCurrencyConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCurrency}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter a new password for <strong>{resetPasswordUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={!newPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Confirm Bulk Delete
            </DialogTitle>
            <DialogDescription>
              This action is <strong className="text-red-500">IRREVERSIBLE</strong>. Are you sure you want to delete{' '}
              <strong className="text-red-500">{bulkDeletePreview?.count || 0}</strong> inventory items?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            <p>Items where <strong>{bulkDeletePreview?.date_field}</strong> &gt;= <strong>{bulkDeletePreview?.after_datetime}</strong></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDeleteExecute} disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrashIcon className="w-4 h-4 mr-2" />}
              Yes, Delete {bulkDeletePreview?.count || 0} Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
